import { isStorageMediaReference, parsePartnerStorageReference } from '@/utils/media/partnerStorageReference';
import { resolveTerminalChecklistMedia } from '@/services/sync/ChecklistMediaConflictResolution';
import { retryAcknowledgedChecklistMediaObservations } from '@/services/sync/ChecklistMediaObservationAcknowledgement';
import { finalizeAcknowledgedChecklistMedia } from '@/services/sync/ChecklistMediaAcknowledgement';
import { assertChecklistNotCancelled } from '@/services/sync/ChecklistCancellationRuntime';
import { createMediaDiagnosticRecorder, createMediaSyncDiagnosticRecorder } from '@/services/diagnostics/mediaDiagnosticRuntime';
import { databaseService } from '../../services/databaseService';
import { APP_CONSTANTS } from '../../config/constants';
import { v4 as uuid } from 'uuid';
import { deleteLocalFile, fetchFile, fileExistsAsync } from './fileHelpers';
import { resolveIOSChecklistMediaSource, readRelocatedIOSChecklistMediaSource } from './iosChecklistMediaSource';
import { UploadImageHelperLogger } from '../../utils/logger';
import { debugPause } from '../debugPauseUniversal';
import { traceImage } from '@/utils/debugImageTrace';
import {
  addSentryBreadcrumb,
  captureSentryExceptionWithScope,
  captureSentryMessageWithScope,
} from '../../services/monitoring/sentry';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '@/config/recoveryDiagnostic';
import { checklistAnswerValue, hasAnswerObservation, checklistAnswerMatchesRow, isAnswerPatch } from '@/services/checklist/checklistObservations';
import {
  assertPendingSyncSessionCurrent,
  capturePendingSyncSession,
  capturePendingWorkOrigin,
  createPendingSyncSupabaseClient,
  isPendingSyncSessionCurrent,
  isPendingSyncSessionSnapshotStateCurrent,
  type PendingSyncSessionSnapshot,
} from '@/services/sync/PendingSyncSession';

// Configurar timeout de rede a partir das constantes
const NETWORK_TIMEOUT = APP_CONSTANTS.TIMEOUTS.NETWORK_TIMEOUT;
const withNetworkTimeout = <T>(promise: Promise<T>, ms: number = NETWORK_TIMEOUT): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(`Network timeout after ${ms} ms`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId) clearTimeout(timeoutId);
  });
};

/**
 * Interface para os parâmetros do upload de imagem
 */
interface UploadImageParams {
  localUri: string;
  itemType: 'USER' | 'COMPANY' | 'CHECKLIST';
  itemId: string;          // id do usuário, empresa ou checklist
  columnName?: string;     // obrigatório p/ USER & COMPANY
  campoId?: string;        // obrigatório p/ CHECKLIST
  pendingImageId?: string;
  tombstoneBarriers?: Map<string, Set<string>>;
  onLocalPersisted?: (uri: string) => void;
  previousLocalUri?: string | null;
  previousImageUrl?: string | null;
  pendingSessionSnapshot?: PendingSyncSessionSnapshot;
  pendingQueueOrigin?: { userId: string; empresaId: string } | null;
}

/**
 * Interface para os dados de pendência de imagem
 */
interface PendingImage {
  id: string;
  item_type: 'USER' | 'COMPANY' | 'CHECKLIST';
  item_id: string;
  local_uri: string;
  bucket: string;
  path: string;
  column_name?: string;
  campo_id?: string;
  user_id?: string | null;
  empresa_id?: string | null;
  baseline_captured?: number | null;
  expected_partner_revision?: number | null;
  expected_remote_value?: string | null;
  expected_remote_updated_at?: string | null;
  prepared_public_url?: string | null;
  created_at: number;
}

/**
 * Interface para as pendências de deleção
 */
interface PendingDeletion {
  id: string;
  type: 'LOCAL' | 'REMOTE';
  path: string;
  itemType: 'USER' | 'COMPANY' | 'CHECKLIST';
  itemId: string;
  campoId?: string | null;
  campo_id?: string | null;
  user_id?: string | null;
  empresa_id?: string | null;
  createdAt: number;
}

export type PendingProcessContext = 'images' | 'deletions';

export interface PendingProcessProgress {
  deferred?: number;
  context: PendingProcessContext;
  processed: number;
  total: number;
  success: number;
  errors: number;
}

export interface PendingProcessResult {
  deferred?: number;
  total: number;
  processed: number;
  success: number;
  errors: number;
}

export interface PendingProcessOptions {
  onProgress?: (progress: PendingProcessProgress) => void;
  allowedPendingIds?: Set<string>;
  tombstoneBarriers?: Map<string, Set<string>>;
  sessionSnapshot?: PendingSyncSessionSnapshot;
}

/**
 * Interface for delete image parameters
 */
interface DeleteImageParams {
  itemType: 'USER' | 'COMPANY' | 'CHECKLIST';
  itemId: string;
  columnName?: string; // obrigatório para USER/COMPANY
  localPathColumn?: string; // obrigatório para USER/COMPANY
  campoId?: string; // obrigatório para CHECKLIST
  previousLocalUri?: string | null;
  previousImageUrl?: string | null;
}

/**
 * Extrai bucket e path de uma URL pública do Supabase Storage
 * @param url URL pública do storage
 * @returns Objeto com bucket e path
 */
export function extractPathFromUrl(url: string): { bucket: string; path: string } {
  // Exemplo de URL: https://<domínio>/storage/v1/object/public/appkit/user-photos/123/avatar.jpg
  UploadImageHelperLogger.Utils.info('Extraindo path de URL pública');
  const parts = url.split('/storage/v1/object/public/');
  if (parts.length < 2) {
    UploadImageHelperLogger.Utils.error('URL inválida para extração de path');
    throw new Error('URL inválida para extração de path');
  }

  const [_, rest] = parts;
  const [bucket, ...pathParts] = rest.split('/');
  const path = pathParts.join('/');

  UploadImageHelperLogger.Utils.info(`Bucket extraído: ${bucket}`);
  return { bucket, path };
}

// Detecta erros de conflito (409) retornados pelo Storage para reaproveitar uploads já existentes
const isResourceAlreadyExistsError = (err: any): boolean => {
  if (!err) return false;
  const statusCode = err.statusCode ?? err?.error?.statusCode ?? err?.originalError?.httpStatusCode;
  const code = err?.code ?? err?.error;
  const message = typeof err?.message === 'string' ? err.message.toLowerCase() : '';
  return statusCode === 409 || code === 'KeyAlreadyExists' || message.includes('already exists');
};

const isStorageObjectMissingError = (error: any): boolean => {
  if (!error) return false;
  const statusCode = Number(error.statusCode ?? error.status ?? error?.error?.statusCode);
  const name = String(error.name ?? error.error ?? '').toLowerCase();
  const message = String(error.message ?? '').toLowerCase();
  return statusCode === 404 || name.includes('notfound') || /object.*not found|not found.*object|no such object/.test(message);
};

const removeRemoteStorageObject = async (
  client: ReturnType<typeof createPendingSyncSupabaseClient>,
  bucket: string,
  path: string,
  snapshot: PendingSyncSessionSnapshot,
): Promise<void> => {
  await assertPendingSyncSessionCurrent(snapshot);
  if (!isPendingSyncSessionSnapshotStateCurrent(snapshot)) {
    throw new Error('Sessão mudou antes da remoção remota; pendência preservada.');
  }
  const { data, error } = await withNetworkTimeout(client.storage.from(bucket).remove([path]));
  if (error) throw error;
  await assertPendingSyncSessionCurrent(snapshot);

  const removed = Array.isArray(data) && data.length === 1 && data[0]?.name === path;
  if (removed) return;

  if (!isPendingSyncSessionSnapshotStateCurrent(snapshot)) {
    throw new Error('Sessão mudou antes da confirmação da remoção; pendência preservada.');
  }
  const infoResult = await withNetworkTimeout(client.storage.from(bucket).info(path));
  await assertPendingSyncSessionCurrent(snapshot);
  if (!isStorageObjectMissingError(infoResult?.error)) {
    if (infoResult?.error) throw infoResult.error;
    throw new Error('Storage não confirmou a remoção do objeto; pendência preservada.');
  }
};

const activeUploadTokens = new Map<string, string>();
const buildUploadKey = (
  itemType: UploadImageParams['itemType'],
  itemId: string,
  campoId?: string,
  columnName?: string
): string => `${itemType}:${itemId}:${campoId ?? columnName ?? 'default'}`;

const isRemoteChecklistValue = (value?: string | null): boolean =>
  Boolean(value && (value.startsWith('http://') || value.startsWith('https://') || parsePartnerStorageReference(value)));

const summarizeUriForTelemetry = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return 'remote-url';
  }
  if (value.startsWith('file://') || value.startsWith('blob:') || value.startsWith('data:')) {
    return 'local-uri';
  }
  if (value.startsWith('checklist-') || value.startsWith('/')) {
    return 'local-path';
  }
  return `value-length-${value.length}`;
};

const hasTombstoneBarrier = (
  barriers: Map<string, Set<string>> | undefined,
  tableName: string,
  itemId?: string | null
): boolean => Boolean(itemId && barriers?.get(tableName)?.has(itemId));

const normalizeChecklistMediaId = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const pendingQueueOriginFields = (
  origin: { userId: string; empresaId: string } | null
): { user_id: string | null; empresa_id: string | null } => ({
  user_id: origin?.userId ?? null,
  empresa_id: origin?.empresaId ?? null,
});

const pendingOwnedBy = (
  pending: {
    item_type?: string;
    itemType?: string;
    user_id?: string | null;
    empresa_id?: string | null;
  },
  snapshot: PendingSyncSessionSnapshot
): boolean => {
  const itemType = String(pending.item_type ?? pending.itemType ?? '').toUpperCase();
  if (itemType === 'CHECKLIST') {
    const companyId = pending.empresa_id;
    if (companyId === null || companyId === undefined || companyId === '') return true;
    return typeof companyId === 'string' && companyId.trim() === snapshot.empresaId;
  }
  return pending.user_id === snapshot.userId && pending.empresa_id === snapshot.empresaId;
};

const samePendingWorkOrigin = (
  left: { userId: string; empresaId: string } | null | undefined,
  right: { userId: string; empresaId: string } | null | undefined,
): boolean => Boolean(left && right && left.userId === right.userId && left.empresaId === right.empresaId);

const isPendingWorkOriginCurrent = (
  origin: { userId: string; empresaId: string } | null | undefined,
): boolean => samePendingWorkOrigin(origin, capturePendingWorkOrigin());

const assertPendingWorkOriginCurrent = (
  origin: { userId: string; empresaId: string } | null | undefined,
): void => {
  if (!isPendingWorkOriginCurrent(origin)) {
    throw new Error('A autoria local mudou durante a operação de mídia.');
  }
};

const pendingSessionStateGuard = (snapshot: PendingSyncSessionSnapshot): (() => boolean) => (
  () => isPendingSyncSessionSnapshotStateCurrent(snapshot)
);

const updateImageRowIfCurrent = async (
  tableName: string,
  itemId: string,
  updates: Record<string, unknown>,
  origin: { userId: string; empresaId: string } | null,
  snapshot: PendingSyncSessionSnapshot | null,
  skipPendingOperation: boolean,
): Promise<boolean> => {
  if (!origin || (snapshot && (origin.userId !== snapshot.userId || origin.empresaId !== snapshot.empresaId))) {
    return false;
  }
  const current = await databaseService.readItem<Record<string, unknown>>(tableName, itemId);
  if (!current || current.id !== itemId) return false;
  if (snapshot) await assertPendingSyncSessionCurrent(snapshot);
  else if (!isPendingWorkOriginCurrent(origin)) return false;

  const expected: Record<string, unknown> = { id: itemId };
  Object.keys(updates).forEach(key => { expected[key] = current[key] ?? null; });
  return databaseService.updateItemIfUnchanged(
    tableName,
    itemId,
    updates,
    expected,
    skipPendingOperation,
    snapshot ? pendingSessionStateGuard(snapshot) : () => isPendingWorkOriginCurrent(origin),
  );
};

const resolvePendingSessionSnapshot = async (
  supplied?: PendingSyncSessionSnapshot
): Promise<PendingSyncSessionSnapshot | null> => {
  const snapshot = supplied ?? await capturePendingSyncSession();
  if (!snapshot) return null;
  await assertPendingSyncSessionCurrent(snapshot);
  return snapshot;
};

export interface PendingRemoteDeletionInput {
  path: string;
  itemType: PendingDeletion['itemType'];
  itemId: string;
  campoId?: string | null;
}

const pendingRemoteDeletionLocks = new Map<string, Promise<void>>();

const runWithPendingRemoteDeletionLock = async <T>(
  key: string,
  operation: () => Promise<T>,
): Promise<T> => {
  const previous = pendingRemoteDeletionLocks.get(key) ?? Promise.resolve();
  let release!: () => void;
  const current = new Promise<void>(resolve => {
    release = resolve;
  });
  pendingRemoteDeletionLocks.set(key, current);

  try {
    await previous;
    return await operation();
  } finally {
    release();
    if (pendingRemoteDeletionLocks.get(key) === current) {
      pendingRemoteDeletionLocks.delete(key);
    }
  }
};

/**
 * Registra uma remoção remota sem perder a identidade do campo de checklist.
 *
 * A fila é local e não deve gerar outra operação de sincronização. Quando a
 * fila já contém a mesma remoção, apenas completa campo_id se a informação
 * ainda estiver ausente; uma identidade diferente nunca é sobrescrita.
 */
const upsertPendingRemoteDeletionUnlocked = async ({
  path,
  itemType,
  itemId,
  campoId,
}: PendingRemoteDeletionInput, origin: { userId: string; empresaId: string } | null): Promise<void> => {
  const tableName = APP_CONSTANTS.DATABASE.TABLES.PENDING_DELETIONS.NAME;
  const normalizedCampoId = itemType === 'CHECKLIST'
    ? normalizeChecklistMediaId(campoId)
    : null;
  const existing = await databaseService.listItems<PendingDeletion>(tableName, {
    type: 'REMOTE',
    path,
    itemType,
    itemId,
  });

  const sameOrigin = existing.filter(pending =>
    pending.user_id === (origin?.userId ?? null) &&
    pending.empresa_id === (origin?.empresaId ?? null)
  );

  if (sameOrigin.length > 0) {
    for (const pending of sameOrigin) {
      const existingCampoId = normalizeChecklistMediaId(pending.campo_id ?? pending.campoId);
      if (normalizedCampoId && !existingCampoId) {
        await databaseService.updateItem(
          tableName,
          pending.id,
          { campo_id: normalizedCampoId },
          true
        );
      } else if (normalizedCampoId && existingCampoId && existingCampoId !== normalizedCampoId) {
        UploadImageHelperLogger.Delete.warn(
          `Pendência remota já possui campo_id divergente; mantendo ${existingCampoId}`
        );
      }
    }
    return;
  }

  const pending: PendingDeletion = {
    id: uuid(),
    type: 'REMOTE',
    path,
    itemType,
    itemId,
    ...pendingQueueOriginFields(origin),
    createdAt: Date.now(),
  };
  if (normalizedCampoId) {
    pending.campo_id = normalizedCampoId;
  }

  await databaseService.createItem(tableName, pending, true);
};

export const upsertPendingRemoteDeletion = async (
  input: PendingRemoteDeletionInput,
  capturedOrigin?: { userId: string; empresaId: string } | null,
): Promise<void> => {
  const origin = capturedOrigin === undefined ? capturePendingWorkOrigin() : capturedOrigin;
  return runWithPendingRemoteDeletionLock(
    JSON.stringify(['REMOTE', input.itemType, input.itemId, input.path, origin?.userId ?? null, origin?.empresaId ?? null]),
    () => upsertPendingRemoteDeletionUnlocked(input, origin),
  );
};

const CHECKLIST_MEDIA_UUID_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';
const CHECKLIST_MEDIA_PATH_PATTERN = new RegExp(
  `^(checklists-(?:photos|signatures|marcacoes))\\/(${CHECKLIST_MEDIA_UUID_PATTERN})\\/(${CHECKLIST_MEDIA_UUID_PATTERN})-(${CHECKLIST_MEDIA_UUID_PATTERN})-([0-9]+)-(${CHECKLIST_MEDIA_UUID_PATTERN})\\.(jpg|jpeg|png|gif|webp)$`,
  'i'
);

export interface ChecklistMediaPathIdentity {
  folder: 'checklists-photos' | 'checklists-signatures' | 'checklists-marcacoes';
  empresaId: string;
  checklistId: string;
  campoId: string;
}

/**
 * Extrai a identidade do checklist de um caminho gerado pelo aplicativo.
 *
 * O nome físico é validado inteiro para que um caminho de outro objeto não
 * possa ser reinterpretado como mídia do checklist apenas por conter UUIDs.
 */
export const parseChecklistMediaPath = (
  path: string
): ChecklistMediaPathIdentity | null => {
  if (typeof path !== 'string') {
    return null;
  }

  const match = CHECKLIST_MEDIA_PATH_PATTERN.exec(path);
  if (!match) {
    return null;
  }

  const [, folder, empresaId, checklistId, campoId] = match;
  return {
    folder: folder as ChecklistMediaPathIdentity['folder'],
    empresaId: empresaId.toLowerCase(),
    checklistId: checklistId.toLowerCase(),
    campoId: campoId.toLowerCase(),
  };
};

const isChecklistMediaPathInScope = (
  path: string,
  checklistId: string,
  empresaId: string,
): boolean => {
  const identity = parseChecklistMediaPath(path);
  if (identity) {
    return identity.empresaId === empresaId.toLowerCase() &&
      identity.checklistId === checklistId.toLowerCase();
  }

  // Formato legado: mantém limites exatos de pasta, tenant e fronteira do checklist.
  const legacyPath = /^checklists-(?:photos|signatures|marcacoes)\/([^/]+)\/([^/]+)$/i.exec(path);
  const legacyFilename = legacyPath?.[2].toLowerCase() ?? '';
  const checklistIdPrefix = checklistId.toLowerCase();
  const namesChecklist = legacyFilename === checklistIdPrefix ||
    legacyFilename.startsWith(`${checklistIdPrefix}-`) || legacyFilename.startsWith(`${checklistIdPrefix}.`);
  return Boolean(legacyPath && legacyPath[1].toLowerCase() === empresaId.toLowerCase() && namesChecklist);
};

const isLegacyChecklistMediaPathInCompany = (path: string, empresaId: string): boolean => {
  const legacyPath = /^checklists-(?:photos|signatures|marcacoes)\/([^/]+)\/([^/]+)$/i.exec(path);
  return Boolean(legacyPath && legacyPath[1].toLowerCase() === empresaId.toLowerCase());
};

export interface ChecklistMediaAncestry {
  checklistId: string;
  campoId: string;
  templateId: string;
}

/**
 * Resolve o encadeamento local necessário para qualquer escrita de mídia de checklist.
 * O campo é obrigatório nesta fronteira para que a barreira do pai também possa ser
 * verificada; pendências antigas sem ele devem permanecer fechadas localmente.
 */
export const resolveChecklistMediaAncestry = async (
  checklistId: string,
  campoId: string | null | undefined
): Promise<ChecklistMediaAncestry> => {
  const normalizedChecklistId = normalizeChecklistMediaId(checklistId);
  const normalizedCampoId = normalizeChecklistMediaId(campoId);
  if (!normalizedChecklistId || !normalizedCampoId) {
    throw new Error('Checklist ou campo ausente para resolver ancestralidade da mídia.');
  }

  const tables = APP_CONSTANTS.DATABASE.TABLES;
  const checklist = await databaseService.readItem<{
    template_id?: string | null;
    snapshot_id?: string | null;
    empresa_id?: string | null;
  }>(tables.GUINCHO_CHECKLISTS.NAME, normalizedChecklistId);
  let originalCampoId = normalizedCampoId;
  let field = await databaseService.readItem<{
    template_id?: string | null;
    empresa_id?: string | null;
  }>(tables.GUINCHO_CAMPOS_TEMPLATE.NAME, originalCampoId);

  // A fila usa o campo visual do snapshot; a barreira continua sendo do campo original.
  if (!field && checklist?.snapshot_id) {
    const snapshotField = await databaseService.readItem<{
      snapshot_id?: string | null;
      original_campo_id?: string | null;
      empresa_id?: string | null;
    }>(tables.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME, normalizedCampoId);
    const empresaId = normalizeChecklistMediaId(checklist.empresa_id);
    const snapshotOriginalId = normalizeChecklistMediaId(snapshotField?.original_campo_id);
    if (
      !empresaId || !snapshotOriginalId ||
      normalizeChecklistMediaId(snapshotField?.empresa_id) !== empresaId ||
      normalizeChecklistMediaId(snapshotField?.snapshot_id) !== normalizeChecklistMediaId(checklist.snapshot_id)
    ) {
      throw new Error('Campo de snapshot não pertence ao checklist/empresa da mídia.');
    }
    originalCampoId = snapshotOriginalId;
    field = await databaseService.readItem(tables.GUINCHO_CAMPOS_TEMPLATE.NAME, originalCampoId);
    if (normalizeChecklistMediaId(field?.empresa_id) !== empresaId) {
      throw new Error('Campo original do snapshot não pertence à empresa da mídia.');
    }
  }

  const checklistTemplateId = normalizeChecklistMediaId(checklist?.template_id);
  const fieldTemplateId = normalizeChecklistMediaId(field?.template_id);
  if (!checklist || !field || !checklistTemplateId || !fieldTemplateId) {
    throw new Error('Ancestral local de checklist/campo não verificável para mídia.');
  }
  if (checklistTemplateId !== fieldTemplateId) {
    throw new Error('Checklist e campo apontam para templates ancestrais diferentes.');
  }

  return {
    checklistId: normalizedChecklistId,
    campoId: originalCampoId,
    templateId: checklistTemplateId,
  };
};

export const hasChecklistMediaTombstone = (
  barriers: Map<string, Set<string>>,
  ancestry: ChecklistMediaAncestry
): boolean => {
  const tables = APP_CONSTANTS.DATABASE.TABLES;
  return Boolean(
    hasTombstoneBarrier(barriers, tables.GUINCHO_CHECKLISTS.NAME, ancestry.checklistId) ||
    hasTombstoneBarrier(barriers, tables.GUINCHO_CAMPOS_TEMPLATE.NAME, ancestry.campoId) ||
    hasTombstoneBarrier(barriers, tables.TEMPLATES_CHECKLIST.NAME, ancestry.templateId)
  );
};

interface LocalChecklistValueTombstone {
  empresa_id?: unknown;
  entity_type?: unknown;
  entity_id?: unknown;
  parent_id?: unknown;
  deletion_kind?: unknown;
  metadata?: unknown;
}

const isNonEmptyChecklistMediaId = (value: unknown): value is string => (
  typeof value === 'string' && value.trim().length > 0
);

const hasExactChecklistValueTombstone = async ({
  empresaId,
  checklistId,
  campoId,
  snapshotFieldId,
}: {
  empresaId: string;
  checklistId: string;
  campoId: string;
  snapshotFieldId: string | null;
}): Promise<boolean> => {
  const valuesTable = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME;
  const tombstones = await databaseService.listItems<LocalChecklistValueTombstone>(
    APP_CONSTANTS.DATABASE.TABLES.SYNC_TOMBSTONES.NAME,
    {
      empresa_id: empresaId,
      entity_type: valuesTable,
      parent_id: checklistId,
    }
  );

  if (!Array.isArray(tombstones)) {
    return false;
  }

  const scopedTombstones = tombstones.filter(row => (
    isNonEmptyChecklistMediaId(row?.empresa_id) &&
    row.empresa_id.toLowerCase() === empresaId &&
    row?.entity_type === valuesTable &&
    row?.parent_id === checklistId &&
    isNonEmptyChecklistMediaId(row?.entity_id)
  ));

  if (scopedTombstones.length === 0) {
    return false;
  }

  const parsed = scopedTombstones.map(row => {
    if (typeof row.metadata !== 'string' || !row.metadata.trim()) {
      return null;
    }

    try {
      const metadata = JSON.parse(row.metadata) as unknown;
      if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
        return null;
      }
      const metadataRecord = metadata as Record<string, unknown>;
      const rawCampoId = metadataRecord.campo_id;
      const metadataCampoId = rawCampoId === null || rawCampoId === undefined
        ? null
        : normalizeChecklistMediaId(rawCampoId);
      if (rawCampoId !== null && rawCampoId !== undefined && !metadataCampoId) {
        return null;
      }

      const rawSnapshotFieldId = metadataRecord.snapshot_field_id;
      const metadataSnapshotFieldId = rawSnapshotFieldId === null || rawSnapshotFieldId === undefined
        ? null
        : normalizeChecklistMediaId(rawSnapshotFieldId);
      if (rawSnapshotFieldId !== null && rawSnapshotFieldId !== undefined && !metadataSnapshotFieldId) {
        return null;
      }

      return {
        campoId: metadataCampoId?.toLowerCase() ?? null,
        snapshotFieldId: metadataSnapshotFieldId?.toLowerCase() ?? null,
        deletionKind: row.deletion_kind,
      };
    } catch {
      return null;
    }
  });

  // Uma row malformada no mesmo checklist impede usar o conjunto como prova.
  if (parsed.some(row => row === null)) {
    return false;
  }

  const matching = snapshotFieldId
    ? parsed.filter(row => row?.snapshotFieldId === snapshotFieldId)
    : parsed.filter(row => row?.campoId === campoId && row.snapshotFieldId === null);

  // O tombstone confirma apenas a terminalidade do valor. O path exato já
  // foi validado separadamente pelo parser canônico da fila local.
  return matching.length === 1 &&
    matching[0]?.deletionKind === 'delete' &&
    (snapshotFieldId ? matching[0]?.campoId === null || matching[0]?.campoId === campoId : true);
};

const canSafelyRemoveRetiredChecklistMedia = async (
  pending: PendingDeletion,
  barriers: Map<string, Set<string>>,
  pathIdentity: ChecklistMediaPathIdentity
): Promise<boolean> => {
  const tables = APP_CONSTANTS.DATABASE.TABLES;
  const checklistTable = tables.GUINCHO_CHECKLISTS.NAME;

  if (
    pending.itemType !== 'CHECKLIST' ||
    pending.type !== 'REMOTE' ||
    pathIdentity.checklistId !== normalizeChecklistMediaId(pending.itemId)?.toLowerCase()
  ) {
    return false;
  }

  if (!hasTombstoneBarrier(barriers, checklistTable, pathIdentity.checklistId)) {
    return false;
  }

  const checklist = await databaseService.readItem<any>(
    checklistTable,
    pathIdentity.checklistId
  );
  if (
    !checklist ||
    normalizeChecklistMediaId(checklist.id)?.toLowerCase() !== pathIdentity.checklistId
  ) {
    return false;
  }

  const checklistEmpresaId = normalizeChecklistMediaId(checklist.empresa_id)?.toLowerCase();
  const checklistTemplateId = normalizeChecklistMediaId(checklist.template_id)?.toLowerCase();
  const checklistSnapshotId = normalizeChecklistMediaId(checklist.snapshot_id)?.toLowerCase() ?? null;
  const retiredAt = checklist.retired_at;
  if (
    !checklistEmpresaId ||
    checklistEmpresaId !== pathIdentity.empresaId ||
    retiredAt === null ||
    retiredAt === undefined ||
    String(retiredAt).trim() === '' ||
    !checklistTemplateId
  ) {
    return false;
  }

  const pendingCampoId = normalizeChecklistMediaId(pending.campoId ?? pending.campo_id)?.toLowerCase();
  let originalCampoId = pathIdentity.campoId;
  let snapshotFieldId: string | null = null;

  if (checklistSnapshotId) {
    const snapshotField = await databaseService.readItem<any>(
      tables.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME,
      pathIdentity.campoId
    );
    const localSnapshotFieldId = normalizeChecklistMediaId(snapshotField?.id)?.toLowerCase();
    const snapshotEmpresaId = normalizeChecklistMediaId(snapshotField?.empresa_id)?.toLowerCase();
    const localSnapshotId = normalizeChecklistMediaId(snapshotField?.snapshot_id)?.toLowerCase();
    const localOriginalCampoId = normalizeChecklistMediaId(snapshotField?.original_campo_id)?.toLowerCase();
    if (
      !snapshotField ||
      localSnapshotFieldId !== pathIdentity.campoId ||
      snapshotEmpresaId !== pathIdentity.empresaId ||
      localSnapshotId !== checklistSnapshotId ||
      !localOriginalCampoId
    ) {
      return false;
    }
    originalCampoId = localOriginalCampoId;
    snapshotFieldId = pathIdentity.campoId;
  } else if (pendingCampoId && pendingCampoId !== pathIdentity.campoId) {
    return false;
  }

  if (
    pendingCampoId &&
    pendingCampoId !== pathIdentity.campoId &&
    pendingCampoId !== originalCampoId
  ) {
    return false;
  }

  const field = await databaseService.readItem<any>(
    tables.GUINCHO_CAMPOS_TEMPLATE.NAME,
    originalCampoId
  );
  const fieldId = normalizeChecklistMediaId(field?.id)?.toLowerCase();
  const fieldTemplateId = normalizeChecklistMediaId(field?.template_id)?.toLowerCase();
  const fieldEmpresaId = normalizeChecklistMediaId(field?.empresa_id)?.toLowerCase();
  if (
    !field ||
    fieldId !== originalCampoId ||
    !fieldTemplateId ||
    fieldTemplateId !== checklistTemplateId ||
    fieldEmpresaId !== checklistEmpresaId
  ) {
    return false;
  }

  return hasExactChecklistValueTombstone({
    empresaId: pathIdentity.empresaId,
    checklistId: pathIdentity.checklistId,
    campoId: originalCampoId,
    snapshotFieldId,
  });
};

const requireTombstonePreflight = (
  barriers: Map<string, Set<string>> | undefined
): Map<string, Set<string>> => {
  if (!barriers) {
    throw new Error('Preflight de tombstones ausente para mídia de checklist.');
  }
  return barriers;
};

const resolvePendingMediaCampoId = async (
  checklistId: string,
  explicitCampoId: string | null | undefined,
  expectedLocalUri: string | null | undefined,
  expectedStoragePath: string | null | undefined
): Promise<string> => {
  const directCampoId = normalizeChecklistMediaId(explicitCampoId);
  if (directCampoId) {
    return directCampoId;
  }

  const values = await databaseService.listItems<any>(
    APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
    { checklist_id: checklistId }
  );
  const normalizedLocalUri = normalizeChecklistMediaId(expectedLocalUri);
  const normalizedStoragePath = normalizeChecklistMediaId(expectedStoragePath);
  const matchingCampoIds = new Set<string>();

  for (const value of values) {
    const campoId = normalizeChecklistMediaId(value?.campo_id ?? value?.campoId);
    const storedValue = normalizeChecklistMediaId(value?.valor);
    if (!campoId || !storedValue) {
      continue;
    }

    let matches = Boolean(normalizedLocalUri && storedValue === normalizedLocalUri);
    if (!matches && normalizedStoragePath && storedValue === normalizedStoragePath) {
      matches = true;
    }
    if (!matches && normalizedStoragePath && isRemoteHttpUri(storedValue)) {
      try {
        matches = extractPathFromUrl(storedValue).path === normalizedStoragePath;
      } catch {
        matches = false;
      }
    }
    if (matches) {
      matchingCampoIds.add(campoId);
    }
  }

  if (matchingCampoIds.size !== 1) {
    throw new Error('Campo local da pendência de mídia não pôde ser identificado com segurança.');
  }
  return [...matchingCampoIds][0];
};

const assertPendingChecklistUploadIsSafe = async (
  pending: PendingImage,
  barriers: Map<string, Set<string>> | undefined,
  snapshot: PendingSyncSessionSnapshot,
): Promise<void> => {
  if (pending.item_type === 'USER') {
    if (pending.item_id !== snapshot.userId) {
      throw new Error('Imagem pendente de outro usuário; pendência preservada.');
    }
    return;
  }
  if (pending.item_type === 'COMPANY') {
    if (pending.item_id !== snapshot.empresaId) {
      throw new Error('Imagem pendente de outra empresa; pendência preservada.');
    }
    return;
  }
  if (pending.item_type !== 'CHECKLIST') {
    return;
  }

  const checklist = await databaseService.readItem<any>(
    APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
    pending.item_id,
  );
  const queuedCompanyId = pending.empresa_id === null || pending.empresa_id === undefined || pending.empresa_id === ''
    ? null
    : typeof pending.empresa_id === 'string' ? pending.empresa_id.trim() : false;
  if (!checklist?.empresa_id || checklist.empresa_id !== snapshot.empresaId ||
      queuedCompanyId === false || (queuedCompanyId && queuedCompanyId !== checklist.empresa_id)) {
    throw new Error('Mídia pendente fora da empresa ativa; pendência preservada.');
  }

  const resolvedBarriers = requireTombstonePreflight(barriers);
  const campoId = await resolvePendingMediaCampoId(
    pending.item_id,
    pending.campo_id,
    pending.local_uri,
    pending.path
  );
  const ancestry = await resolveChecklistMediaAncestry(pending.item_id, campoId);
  if (hasChecklistMediaTombstone(resolvedBarriers, ancestry)) {
    throw new Error('Upload de mídia pendente bloqueado por tombstone do checklist/campo/template.');
  }
};

const assertPendingChecklistDeletionIsSafe = async (
  pending: PendingDeletion,
  barriers: Map<string, Set<string>> | undefined
): Promise<boolean> => {
  if (pending.type !== 'REMOTE' || pending.itemType !== 'CHECKLIST') {
    return true;
  }

  const resolvedBarriers = requireTombstonePreflight(barriers);
  let ancestry: ChecklistMediaAncestry;
  try {
    const campoId = await resolvePendingMediaCampoId(
      pending.itemId,
      pending.campoId ?? pending.campo_id,
      null,
      pending.path
    );
    ancestry = await resolveChecklistMediaAncestry(pending.itemId, campoId);
  } catch (error) {
    const checklistTable = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME;
    const currentChecklist = await databaseService.readItem<any>(checklistTable, pending.itemId);
    if (currentChecklist) {
      const pathIdentity = parseChecklistMediaPath(pending.path);
      if (
        pathIdentity &&
        await canSafelyRemoveRetiredChecklistMedia(pending, resolvedBarriers, pathIdentity)
      ) {
        return true;
      }
      throw error;
    }
    if (hasTombstoneBarrier(resolvedBarriers, checklistTable, pending.itemId)) {
      return false;
    }
    const checklistDeletes = await databaseService.listItems<any>('pending_operations', {
      operation: 'delete',
      collection: checklistTable,
      item_id: pending.itemId,
    });
    const hasDurableChecklistDelete = checklistDeletes.some(operation => {
      try {
        const payload = typeof operation.data === 'string'
          ? JSON.parse(operation.data)
          : operation.data;
        return operation.operation === 'delete' &&
          operation.collection === checklistTable &&
          String(operation.item_id ?? operation.itemId ?? '') === pending.itemId &&
          String(payload?.id ?? '') === pending.itemId &&
          Boolean(normalizeChecklistMediaId(payload?.empresa_id ?? payload?.empresaId));
      } catch {
        return false;
      }
    });
    if (hasDurableChecklistDelete) {
      return false;
    }
    throw error;
  }
  if (hasChecklistMediaTombstone(resolvedBarriers, ancestry)) {
    const pathIdentity = parseChecklistMediaPath(pending.path);
    if (
      pathIdentity &&
      await canSafelyRemoveRetiredChecklistMedia(pending, resolvedBarriers, pathIdentity)
    ) {
      return true;
    }
    throw new Error('Deleção remota pendente bloqueada por tombstone do checklist/campo/template.');
  }
  return true;
};

const assertPendingDeletionTargetInScope = async (
  pending: PendingDeletion,
  snapshot: PendingSyncSessionSnapshot,
): Promise<void> => {
  if (pending.itemType === 'CHECKLIST' && pending.empresa_id !== null &&
      pending.empresa_id !== undefined && pending.empresa_id !== '' &&
      (typeof pending.empresa_id !== 'string' || pending.empresa_id.trim() !== snapshot.empresaId)) {
    throw new Error('Exclusão de checklist pertence a outra empresa; pendência preservada.');
  }
  if (pending.itemType === 'USER') {
    if (pending.itemId !== snapshot.userId) {
      throw new Error('Deleção pendente de outro usuário; pendência preservada.');
    }
    return;
  }
  if (pending.itemType === 'COMPANY') {
    if (pending.itemId !== snapshot.empresaId) {
      throw new Error('Deleção pendente de outra empresa; pendência preservada.');
    }
    return;
  }

  if (pending.type === 'REMOTE') {
    const identity = parseChecklistMediaPath(pending.path);
    if (!isChecklistMediaPathInScope(pending.path, pending.itemId, snapshot.empresaId)) {
      throw new Error('Path da mídia não comprova checklist e empresa da pendência.');
    }
    if (identity) return;
    const checklist = await databaseService.readItem<any>(
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
      pending.itemId,
    );
    if (checklist?.empresa_id && checklist.empresa_id !== snapshot.empresaId) {
      throw new Error('Checklist remoto pendente pertence a outra empresa.');
    }
    return;
  }

  const checklist = await databaseService.readItem<any>(
    APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
    pending.itemId,
  );
  if (checklist?.empresa_id && checklist.empresa_id !== snapshot.empresaId) {
    throw new Error('Checklist local pertence a outra empresa; arquivo preservado.');
  }
  const localPath = String(pending.path ?? '').replace(/\\/g, '/');
  const match = /(?:^|\/)checklist-(?:photos|signatures|marcacoes)\/([^/]+)\//i.exec(localPath);
  if (!match || match[1].toLowerCase() !== snapshot.empresaId.toLowerCase()) {
    throw new Error('Caminho local não comprova a empresa da mídia; pendência preservada.');
  }
};

const normalizeUploadDedupePart = (value: unknown, maxLength: number = 180): string => {
  if (value === null || value === undefined) {
    return 'none';
  }
  const normalized = String(value).replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return 'empty';
  }
  return normalized.length > maxLength ? normalized.slice(0, maxLength) : normalized;
};

const getUploadErrorSignatureForDedupe = (error: unknown): string => {
  if (error instanceof Error) {
    const stackLine = error.stack?.split('\n')[1]?.trim() || 'sem_stack';
    return [
      normalizeUploadDedupePart(error.name, 60),
      normalizeUploadDedupePart(error.message, 180),
      normalizeUploadDedupePart(stackLine, 180),
    ].join('|');
  }

  return normalizeUploadDedupePart(error, 220);
};

const buildUploadFailureDedupeKey = ({
  itemType,
  itemId,
  campoId,
  columnName,
  error,
}: {
  itemType: UploadImageParams['itemType'];
  itemId: string;
  campoId?: string;
  columnName?: string;
  error: unknown;
}): string => {
  const fieldKey = campoId || columnName || 'default';
  return [
    'upload:upload_image_helper_exception',
    normalizeUploadDedupePart(itemType, 40),
    normalizeUploadDedupePart(itemId, 120),
    normalizeUploadDedupePart(fieldKey, 120),
    getUploadErrorSignatureForDedupe(error),
  ].join(':');
};

const isRemoteHttpUri = (value?: string | null): boolean =>
  Boolean(value && /^https?:\/\//i.test(value));

const isMissingLocalResourceError = (error: unknown): boolean => {
  const message = error instanceof Error
    ? error.message.toLowerCase()
    : String(error ?? '').toLowerCase();

  return (
    message.includes('imagem não encontrada no indexeddb') ||
    message.includes('arquivo não encontrado') ||
    message.includes('file not found') ||
    message.includes('no such file')
  );
};

const pruneChecklistPartialUpdateLocalUri = async ({
  checklistId,
  campoId,
  localUri,
  snapshot,
}: {
  checklistId: string;
  campoId: string;
  localUri: string;
  snapshot: PendingSyncSessionSnapshot;
}): Promise<void> => {
  const pendingTableName = APP_CONSTANTS.DATABASE.TABLES.PENDING_OPERATIONS.NAME;
  const valuesTableName = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME;

  const pendingOps = await databaseService.listItems<any>(pendingTableName, {
    operation: 'checklist_partial_update',
    collection: valuesTableName,
    item_id: checklistId,
  });

  const ownedPendingOps = pendingOps.filter((op: any) => pendingOwnedBy(op, snapshot));
  if (!ownedPendingOps.length) {
    return;
  }

  for (const op of ownedPendingOps) {
    if (!op?.id || !op?.data) {
      continue;
    }

    let payload: Record<string, unknown> | null = null;
    try {
      if (typeof op.data === 'string') {
        const parsed = JSON.parse(op.data);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          payload = parsed as Record<string, unknown>;
        }
      } else if (typeof op.data === 'object' && !Array.isArray(op.data)) {
        payload = { ...(op.data as Record<string, unknown>) };
      }
    } catch (parseError) {
      UploadImageHelperLogger.Main.warn(
        `[processPendingImages] Falha ao analisar payload da pendência ${op.id} para limpeza defensiva: ${parseError}`
      );
      continue;
    }

    if (!payload || payload[campoId] !== localUri) {
      continue;
    }

    await assertPendingSyncSessionCurrent(snapshot);

    delete payload[campoId];
    const remainingKeys = Object.keys(payload);

    if (remainingKeys.length === 0) {
      await databaseService.deleteItemIfUnchanged(pendingTableName, op.id, {
        user_id: snapshot.userId,
        empresa_id: snapshot.empresaId,
        data: op.data,
      }, true, pendingSessionStateGuard(snapshot));
      UploadImageHelperLogger.Main.warn(
        `[processPendingImages] Pendência checklist_partial_update ${op.id} removida após descarte de localUri morto`
      );
      continue;
    }

    await databaseService.updateItemIfUnchanged(
      pendingTableName,
      op.id,
      {
        data: JSON.stringify(payload),
        timestamp: Date.now(),
      },
      { user_id: snapshot.userId, empresa_id: snapshot.empresaId, data: op.data },
      true,
      pendingSessionStateGuard(snapshot)
    );
    UploadImageHelperLogger.Main.warn(
      `[processPendingImages] Campo ${campoId} removido da pendência checklist_partial_update ${op.id} após descarte de localUri morto`
    );
  }
};

const discardDeadPendingImage = async (
  pending: PendingImage,
  reason: 'local_source_missing' | 'local_source_missing_after_error',
  snapshot: PendingSyncSessionSnapshot,
): Promise<void> => {
  if (pending.item_type === 'CHECKLIST' && pending.campo_id) {
    createMediaDiagnosticRecorder({ checklistId: pending.item_id, fieldId: pending.campo_id, attemptId: pending.id })('source_missing');
  }
  UploadImageHelperLogger.Main.warn(
    `[processPendingImages] Descarta pendência morta ${pending.id} (${reason})`
  );

  if (pending.item_type === 'CHECKLIST' && pending.campo_id) {
    try {
      await pruneChecklistPartialUpdateLocalUri({
        checklistId: pending.item_id,
        campoId: pending.campo_id,
        localUri: pending.local_uri,
        snapshot,
      });
    } catch (cleanupError) {
      UploadImageHelperLogger.Main.warn(
        `[processPendingImages] Falha ao limpar checklist_partial_update da pendência ${pending.id}: ${cleanupError}`
      );
    }
  }

  await assertPendingSyncSessionCurrent(snapshot);
  await databaseService.deleteItemIfUnchanged('pending_images', pending.id, {
    item_type: pending.item_type,
    item_id: pending.item_id,
    campo_id: pending.campo_id ?? null,
    local_uri: pending.local_uri,
    user_id: snapshot.userId,
    empresa_id: snapshot.empresaId,
  }, true, pendingSessionStateGuard(snapshot));

  captureSentryMessageWithScope('Pendência de upload descartada por origem local ausente', {
    level: 'warning',
    context: {
      flow: 'pending_images',
      stage: 'discard_dead_pending',
      pendingId: pending.id,
      reason,
      itemType: pending.item_type,
      itemId: pending.item_id,
      campoId: pending.campo_id ?? null,
      columnName: pending.column_name ?? null,
      localUri: summarizeUriForTelemetry(pending.local_uri),
    },
    tags: {
      flow: 'sync',
      stage: 'pending_images',
      result: 'discarded_dead_pending',
    },
    fingerprint: ['sync', 'pending_images', 'discard_dead_pending'],
    dedupeKey: `sync:pending_images:discard:${pending.id}`,
    dedupeWindowMs: 60000,
  });
};

interface ChecklistValueRecord {
  id: string;
  empresa_id: string;
  checklist_id: string;
  campo_id: string | null;
  snapshot_field_id?: string | null;
  valor: string;
  created_at: number;
  updated_at: number;
  observacao?: string | null;
}

export interface ChecklistValueIdentity {
  /** Relação com o campo atual, quando o snapshot possui essa origem. */
  campoId: string | null;
  /** Identidade visual usada pelo editor histórico. */
  snapshotFieldId: string | null;
}

/**
 * Normaliza a identidade física de um valor antes de gravá-lo localmente.
 *
 * O editor de um checklist com snapshot usa o id do campo do snapshot como
 * identidade visual. Esse id não pode ser gravado em `campo_id`: a coluna é
 * uma FK para `guincho_campos_template` e a guarda de ancestralidade rejeita
 * o insert. O valor precisa manter o campo original (quando houver) e o
 * `snapshot_field_id` separadamente.
 */
export const resolveChecklistValueIdentity = (
  visualFieldId: string,
  snapshotField?: { id?: unknown; original_campo_id?: unknown } | null
): ChecklistValueIdentity => {
  const normalizedVisualFieldId = normalizeChecklistMediaId(visualFieldId);
  if (!normalizedVisualFieldId) {
    throw new Error('Campo ausente para persistir valor de checklist.');
  }

  const normalizedSnapshotFieldId = normalizeChecklistMediaId(snapshotField?.id);
  if (!normalizedSnapshotFieldId) {
    return {
      campoId: normalizedVisualFieldId,
      snapshotFieldId: null,
    };
  }

  return {
    campoId: normalizeChecklistMediaId(snapshotField?.original_campo_id),
    snapshotFieldId: normalizedSnapshotFieldId,
  };
};

const findChecklistSnapshotField = async (
  empresaId: string,
  snapshotId: string | null | undefined,
  visualFieldId: string
): Promise<{ id: string; original_campo_id?: string | null } | undefined> => {
  if (!snapshotId) return undefined;

  const fields = await databaseService.listItems<{ id: string; original_campo_id?: string | null }>(
    APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME,
    { empresa_id: empresaId, snapshot_id: snapshotId }
  );
  return fields.find(field => field.id === visualFieldId)
    ?? fields.find(field => field.original_campo_id === visualFieldId);
};

const matchesChecklistValueIdentity = (
  value: ChecklistValueRecord,
  identity: ChecklistValueIdentity,
  visualFieldId: string
): boolean => {
  if (identity.snapshotFieldId) {
    return value.snapshot_field_id === identity.snapshotFieldId || (
      !value.snapshot_field_id &&
      identity.campoId !== null &&
      value.campo_id === identity.campoId
    ) || (
      !value.snapshot_field_id &&
      value.campo_id === visualFieldId
    );
  }

  return !value.snapshot_field_id && value.campo_id === identity.campoId;
};

const findChecklistValueByIdentity = (
  values: ChecklistValueRecord[],
  identity: ChecklistValueIdentity,
  visualFieldId: string
): ChecklistValueRecord | undefined => {
  if (identity.snapshotFieldId) {
    // A linha canônica sempre vence, independentemente da ordem local.
    return values.find(value => value.snapshot_field_id === identity.snapshotFieldId)
      // Compatibilidade com a primeira forma legada: campo original sem
      // snapshot_field_id.
      ?? values.find(value => (
        !value.snapshot_field_id &&
        identity.campoId !== null &&
        value.campo_id === identity.campoId
      ))
      // Compatibilidade com a linha criada incorretamente com o id visual em
      // campo_id.
      ?? values.find(value => (
        !value.snapshot_field_id && value.campo_id === visualFieldId
      ));
  }

  return values.find(value => (
    !value.snapshot_field_id && value.campo_id === identity.campoId
  ));
};

/**
 * Localiza o valor pelo id visual recebido pelo editor.
 *
 * Em checklists históricos o id visual é o id do campo do snapshot, enquanto
 * valores antigos podem ter sido gravados apenas com o campo original (ou,
 * antes da correção, com o próprio id visual em `campo_id`). A leitura precisa
 * aceitar as três formas para que a substituição/remocão também limpe o
 * recurso anterior no Storage.
 */
export const findChecklistValueForVisualField = async (
  empresaId: string,
  checklistId: string,
  visualFieldId: string,
  snapshotId?: string | null
): Promise<ChecklistValueRecord | undefined> => {
  const normalizedVisualFieldId = normalizeChecklistMediaId(visualFieldId);
  if (!normalizedVisualFieldId) {
    return undefined;
  }

  const snapshotField = await findChecklistSnapshotField(
    empresaId,
    snapshotId,
    normalizedVisualFieldId
  );
  const identity = resolveChecklistValueIdentity(normalizedVisualFieldId, snapshotField);
  const values = await databaseService.listItems<ChecklistValueRecord>(
    APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
    { checklist_id: checklistId }
  );

  return findChecklistValueByIdentity(values, identity, normalizedVisualFieldId);
};

type ChecklistPersistMode = 'create' | 'update' | 'skip';

const persistChecklistValue = async ({
  empresaId,
  checklistId,
  campoId,
  valor,
  mode,
  existing,
  snapshotId,
  snapshotField: providedSnapshotField,
  pendingOrigin,
  canAcknowledge,
}: {
  empresaId: string;
  checklistId: string;
  campoId: string;
  valor: string;
  mode: ChecklistPersistMode;
  existing?: ChecklistValueRecord | null;
  snapshotId?: string | null;
  snapshotField?: { id: string; original_campo_id?: string | null };
  pendingOrigin?: { userId: string; empresaId: string } | null;
  canAcknowledge?: () => boolean;
}): Promise<{ previousValue?: string | null; existed: boolean }> => {
  const tableName = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME;
  const snapshotField = providedSnapshotField
    ?? await findChecklistSnapshotField(empresaId, snapshotId, campoId);
  if (snapshotId && !snapshotField) {
    throw new Error('Campo do snapshot não encontrado para persistir mídia de checklist.');
  }
  const identity = resolveChecklistValueIdentity(campoId, snapshotField);
  const existingRows = existing && matchesChecklistValueIdentity(existing, identity, campoId)
    ? [existing]
    : await databaseService.listItems<ChecklistValueRecord>(tableName, {
      checklist_id: checklistId,
    });
  const cachedExisting = findChecklistValueByIdentity(existingRows, identity, campoId);
  // O writer canônico já transforma o id visual em campo_id original +
  // snapshot_field_id e grava o valor junto com uma única pendência parcial.
  // Isso evita que o upload isolado concorra com um partial update posterior
  // usando outro UUID para a mesma identidade lógica.
  await databaseService.bulkUpsertChecklistValues(
    empresaId,
    checklistId,
    { [campoId]: valor },
    {
      skipPendingOp: mode === 'skip',
      pendingOrigin,
      canAcknowledge,
    }
  );

  return { previousValue: cachedExisting?.valor ?? null, existed: Boolean(cachedExisting) };
};

/**
 * Helper para upload de imagens com suporte a fallback local
 * Esta função implementa o fluxo de upload de imagens conforme documentado
 * @param params Parâmetros do upload
 *   - Para USER/COMPANY: requer columnName, previousLocalUri, previousImageUrl
 *   - Para CHECKLIST: requer campoId
 * @returns Promise com a URL pública e o path no storage
 */
export interface ImageUploadResult {
  publicURL: string;
  storagePath: string;
  localPersisted?: boolean;
  /** Checklist: true somente após confirmar o objeto e a resposta remota por readback. */
  remoteAcknowledged?: boolean;
  superseded?: boolean;
}

const checklistUploadsInFlight = new Map<string, { pending: PendingImage; operation: Promise<ImageUploadResult> }>();
const sameChecklistUploadAttempt = (left: PendingImage, right: PendingImage): boolean => (
  ['id', 'item_type', 'item_id', 'campo_id', 'local_uri', 'bucket', 'path', 'user_id', 'empresa_id', 'created_at', 'expected_partner_revision']
    .every(key => left[key as keyof PendingImage] === right[key as keyof PendingImage])
);

const checklistStoragePath = (localUri: string, legacyPath?: string): string => {
  const match = localUri.match(/(?:^|\/)checklist-(photos|signatures|marcacoes)\/(.+)$/);
  if (!match) {
    if (legacyPath && !/^https?:/.test(legacyPath)) return legacyPath;
    throw new Error('Imagem do checklist sem caminho local durável reconhecido.');
  }
  const folders = APP_CONSTANTS.STORAGE.FOLDER;
  const folder = match[1] === 'photos' ? folders.CHECKLIST_PHOTOS
    : match[1] === 'signatures' ? folders.CHECKLIST_SIGNATURES : folders.CHECKLIST_MARCACOES;
  return `${folder}/${match[2]}`;
};

export async function prepareChecklistMediaUpload(
  checklistId: string, campoId: string, localUri: string, attemptId = uuid(), expectedValue?: string
): Promise<PendingImage> {
  if (isStorageMediaReference(localUri)) throw new Error('Mídia privada remota não pode virar upload local.');
  const origin = capturePendingWorkOrigin();
  const record = createMediaDiagnosticRecorder({ checklistId, fieldId: campoId, attemptId, mediaRef: localUri });
  record('prepare_started');
  try {
    if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) throw new Error('Upload bloqueado pelo modo de recuperacao.');
    if (!checklistId || !campoId || !localUri) throw new Error('Parâmetros obrigatórios não informados');
    record('prepare_reading');
    const checklist = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME, checklistId);
    if (!checklist?.empresa_id) throw new Error('Checklist sem empresa local para persistir mídia pendente.');
    if (origin && origin.empresaId !== checklist.empresa_id) {
      throw new Error('Checklist fora da empresa ativa; pendência de mídia não foi criada.');
    }
    if (checklist.retired_at) throw new Error('media_preparation_superseded');
    const pending = {
      id: attemptId, item_type: 'CHECKLIST' as const, item_id: checklistId, campo_id: campoId,
      local_uri: localUri, bucket: APP_CONSTANTS.STORAGE.BUCKET.CHECKLIST,
    path: checklistStoragePath(localUri), column_name: null,
    ...pendingQueueOriginFields(origin), created_at: Date.now(),
  };
  await databaseService.bulkUpsertChecklistValues(checklist.empresa_id, checklistId, { [campoId]: localUri }, {
    skipPendingOp: true,
    mediaPending: { checklistId, campoId, pending, pendingOrigin: origin, expectedValue, cleanupId: attemptId, onStage: record },
  });
    record('prepared');
    const prepared = await databaseService.readItem<PendingImage>('pending_images', pending.id);
    if (!prepared || prepared.local_uri !== localUri || prepared.item_id !== checklistId ||
        prepared.campo_id !== campoId || prepared.user_id !== pending.user_id ||
        prepared.empresa_id !== pending.empresa_id) throw new Error('Preparação de mídia não confirmou a fila local.');
    return prepared;
  } catch (error) {
    record(error instanceof Error && error.message === 'media_preparation_superseded' ? 'superseded' : 'prepare_failed', error);
    throw error;
  }
}

async function resumeChecklistMediaAcknowledgement(pending: PendingImage, snapshot: PendingSyncSessionSnapshot): Promise<boolean> {
  const record = createMediaSyncDiagnosticRecorder({ companyId: snapshot.empresaId, userId: snapshot.userId,
    checklistId: pending.item_id, fieldId: pending.campo_id ?? '', attemptId: pending.id, mediaRef: pending.local_uri,
  });
  const deferred = (reason: Parameters<typeof record>[1]): false => {
    record('reference_ack_deferred', reason);
    return false;
  };
  try {
    if (pending.item_type !== 'CHECKLIST' || !pendingOwnedBy(pending, snapshot) || !pending.campo_id) return deferred('identity_mismatch');
    const campoId = pending.campo_id;
    const checklist = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME, pending.item_id);
    const pendingCompany = typeof pending.empresa_id === 'string' ? pending.empresa_id.trim() : '';
    if (!checklist || checklist.empresa_id !== snapshot.empresaId ||
        (pendingCompany && pendingCompany !== checklist.empresa_id) ||
        (pending.empresa_id !== null && pending.empresa_id !== undefined && typeof pending.empresa_id !== 'string')) return deferred('identity_mismatch');
    const value = await findChecklistValueForVisualField(snapshot.empresaId, pending.item_id, campoId, checklist.snapshot_id);
    const path = checklistStoragePath(pending.local_uri, pending.path);
    const legacyOriginMissing = !pending.user_id || !pendingCompany;
    const legacyCompanyPath = legacyOriginMissing && isLegacyChecklistMediaPathInCompany(path, snapshot.empresaId);
    if (path !== pending.path ||
        (!isChecklistMediaPathInScope(path, pending.item_id, snapshot.empresaId) && !legacyCompanyPath)) return deferred('path_mismatch');
    const publicUrl = createPendingSyncSupabaseClient(snapshot).storage.from(pending.bucket).getPublicUrl(path).data?.publicUrl;
    if (!publicUrl || value?.valor !== publicUrl) return deferred('local_reference_not_prepared');
    const resolution = await resolveTerminalChecklistMedia(pending, snapshot);
    if (resolution === 'resolved') return true;
    if (resolution === 'pending') throw new Error('Resolução terminal da mídia ainda inconclusiva; fila e bytes preservados.');
    const snapshotField = await findChecklistSnapshotField(snapshot.empresaId, checklist.snapshot_id, campoId);
    const identity = resolveChecklistValueIdentity(campoId, snapshotField);
    if (legacyCompanyPath) {
      const linkedValues = await databaseService.listItems<ChecklistValueRecord>(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
        { checklist_id: pending.item_id }
      );
      const exactLinks = linkedValues.filter(row => row.empresa_id === snapshot.empresaId &&
        row.checklist_id === pending.item_id && matchesChecklistValueIdentity(row, identity, campoId));
      if (exactLinks.length !== 1 || exactLinks[0].id !== value.id || exactLinks[0].valor !== publicUrl) return deferred('identity_mismatch');
    }
    const client = createPendingSyncSupabaseClient(snapshot);
    await assertPendingSyncSessionCurrent(snapshot);
    const { data: remote, error } = await client.from('guincho_valores_checklist')
      .select('id,empresa_id,checklist_id,campo_id,snapshot_field_id,valor,observacao,created_at,updated_at')
      .eq('id', value.id).eq('empresa_id', snapshot.empresaId).eq('checklist_id', pending.item_id).maybeSingle();
    await assertPendingSyncSessionCurrent(snapshot);
    if (error) throw error;
    const hasBaselineVersion = typeof pending.expected_remote_updated_at === 'string' &&
      Number.isFinite(Date.parse(pending.expected_remote_updated_at));
    // O objeto pode existir antes de o push gravar a resposta. Nesse ponto a
    // pendência parcial ainda precisa avançar; não transformar ausência em erro.
    if (!remote) {
      if (pending.baseline_captured === 1 && ((pending.expected_remote_value !== null &&
          pending.expected_remote_value !== undefined) || hasBaselineVersion)) {
        throw new Error('Valor remoto anterior desapareceu; fila preservada.');
      }
      return deferred('remote_reference_missing');
    }
    if (remote.id !== value.id || remote.empresa_id !== snapshot.empresaId || remote.checklist_id !== pending.item_id ||
        !matchesChecklistValueIdentity(remote, identity, campoId)) {
      throw new Error('Resposta remota diverge da mídia preparada; fila preservada.');
    }
    if (remote.valor !== publicUrl) {
      const remoteVersionChanged = typeof remote.updated_at === 'string' && Number.isFinite(Date.parse(remote.updated_at)) &&
        hasBaselineVersion && Date.parse(remote.updated_at) !== Date.parse(pending.expected_remote_updated_at!);
      if (pending.baseline_captured !== 1 || !hasBaselineVersion || pending.expected_remote_value === undefined ||
          (remote.valor !== pending.expected_remote_value && !remoteVersionChanged)) {
        throw new Error('Resposta remota diverge da versão anterior da mídia; fila preservada.');
      }
      // Outra versão precisa passar pela RPC com o CAS original para comprovar o conflito.
      return deferred('remote_reference_mismatch');
    }
    if (!await finalizeAcknowledgedChecklistMedia({ pending, value, publicUrl, snapshot })) {
      throw new Error('ACK remoto exato não conseguiu confirmar o CAS local; fila preservada.');
    }
    return true;
  } catch (error) {
    record('reference_ack_failed', error instanceof Error && error.name === 'PendingSyncSessionChangedError'
      ? 'session_changed' : 'ack_exception', error);
    throw error;
  }
}

/** Reconhece somente a intenção local exata que ainda depende da preparação da URL. */
async function checklistMediaAwaitingPreparation(pending: PendingImage, snapshot: PendingSyncSessionSnapshot): Promise<boolean> {
  if (pending.item_type !== 'CHECKLIST' || !pending.campo_id || pending.bucket !== APP_CONSTANTS.STORAGE.BUCKET.CHECKLIST || !Number.isFinite(pending.created_at) ||
      typeof pending.user_id !== 'string' || !pending.user_id.trim() || pending.empresa_id !== snapshot.empresaId ||
      !/^(?:(?:file|content|blob|temp|data):|\/|\.{1,2}\/|checklist-(?:photos|signatures|marcacoes)\/)/i.test(pending.local_uri)) return false;
  const checklist = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME, pending.item_id);
  if (!checklist || checklist.empresa_id !== snapshot.empresaId || checklist.retired_at) return false;
  const path = checklistStoragePath(pending.local_uri, pending.path);
  const pathIdentity = parseChecklistMediaPath(path);
  if (path !== pending.path || !isChecklistMediaPathInScope(path, pending.item_id, snapshot.empresaId) ||
      (pathIdentity && pathIdentity.campoId !== pending.campo_id.toLowerCase())) return false;
  const publicUrl = createPendingSyncSupabaseClient(snapshot).storage.from(pending.bucket).getPublicUrl(path).data?.publicUrl;
  if (typeof publicUrl !== 'string' || !/^https?:\/\//i.test(publicUrl) ||
      (pending.prepared_public_url != null && pending.prepared_public_url !== publicUrl)) return false;
  let snapshotField: { id: string; original_campo_id?: string | null } | undefined;
  if (checklist.snapshot_id) {
    const parent = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOTS.NAME, checklist.snapshot_id);
    if (!parent || parent.empresa_id !== snapshot.empresaId || parent.checklist_id !== pending.item_id) return false;
    const fields = await databaseService.listItems<{ id: string; original_campo_id?: string | null }>(
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME,
      { empresa_id: snapshot.empresaId, snapshot_id: checklist.snapshot_id });
    const references = fields.filter(field => field.id === pending.campo_id || field.original_campo_id === pending.campo_id);
    if (references.length !== 1) return false;
    snapshotField = references[0];
  } else {
    const fields = await databaseService.listItems<{ id: string; retired_at?: string | null }>(
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME,
      { empresa_id: snapshot.empresaId, template_id: checklist.template_id });
    if (fields.filter(field => field.id === pending.campo_id && !field.retired_at).length !== 1) return false;
  }
  const identity = resolveChecklistValueIdentity(pending.campo_id, snapshotField);
  const values = await databaseService.listItems<ChecklistValueRecord>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
    { checklist_id: pending.item_id });
  const linked = values.filter(value => value.empresa_id === snapshot.empresaId && value.checklist_id === pending.item_id &&
    matchesChecklistValueIdentity(value, identity, pending.campo_id!) &&
    (!identity.snapshotFieldId || value.campo_id === null || (value.campo_id ?? null) === identity.campoId));
  if (linked.length !== 1 || linked[0].valor !== pending.local_uri) return false;
  const value = linked[0];
  const aliases = new Set([pending.campo_id, identity.campoId, identity.snapshotFieldId].filter(Boolean));
  const media = await databaseService.listItems<PendingImage>('pending_images', { item_id: pending.item_id });
  if (media.filter(row => row.item_type === 'CHECKLIST' && row.campo_id && aliases.has(row.campo_id)).length !== 1) return false;
  const operations = await databaseService.getPendingOperations();
  const partials = operations.filter(op => {
    if (!op.id || !Number.isFinite(op.timestamp) || op.operation !== 'checklist_partial_update' ||
        op.collection !== APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME || op.itemId !== pending.item_id ||
        op.user_id !== pending.user_id || op.empresa_id !== pending.empresa_id) return false;
    try {
      const answers = typeof op.data === 'string' ? JSON.parse(op.data) : op.data;
      if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return false;
      const entries = Object.entries(answers).filter(([fieldId]) => aliases.has(fieldId));
      if (entries.length !== 1) return false;
      const answer = entries[0][1];
      const effectiveValue = isAnswerPatch(answer) && !Object.prototype.hasOwnProperty.call(answer, 'valor')
        ? value.valor : checklistAnswerValue(answer);
      return effectiveValue === pending.local_uri && checklistAnswerMatchesRow(answer, value);
    } catch { return false; }
  });
  if (partials.length !== 1) return false;
  const operation = partials[0];
  const currentOperation = await databaseService.readItem<typeof operation & { item_id?: string }>('pending_operations', operation.id);
  const current = await databaseService.readItem<PendingImage>('pending_images', pending.id);
  const currentChecklist = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME, pending.item_id);
  const currentValue = await databaseService.readItem<ChecklistValueRecord>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME, value.id);
  await assertPendingSyncSessionCurrent(snapshot);
  // Releitura conserva a autoria e o snapshot da parcial; alterações posteriores não herdam esta espera.
  return Boolean(currentOperation && current && currentValue && currentChecklist &&
    sameChecklistUploadAttempt(current, pending) && current.prepared_public_url === pending.prepared_public_url &&
    currentChecklist.empresa_id === snapshot.empresaId && !currentChecklist.retired_at && currentChecklist.snapshot_id === checklist.snapshot_id &&
    currentValue.empresa_id === value.empresa_id && currentValue.checklist_id === value.checklist_id &&
    currentValue.campo_id === value.campo_id && currentValue.snapshot_field_id === value.snapshot_field_id &&
    currentValue.valor === pending.local_uri && checklistAnswerMatchesRow({ valor: value.valor, observacao: value.observacao ?? null }, currentValue) &&
    currentOperation.id === operation.id && currentOperation.operation === operation.operation && currentOperation.collection === operation.collection &&
    (currentOperation.itemId ?? currentOperation.item_id) === operation.itemId && currentOperation.timestamp === operation.timestamp &&
    currentOperation.user_id === operation.user_id && currentOperation.empresa_id === operation.empresa_id && currentOperation.data === operation.data);
}

export async function finalizePendingChecklistMedia(snapshot: PendingSyncSessionSnapshot): Promise<{ errors: number; deferred: number }> {
  await retryAcknowledgedChecklistMediaObservations(snapshot);
  const pending = await databaseService.listItems<PendingImage>('pending_images');
  let errors = 0;
  let deferred = 0;
  for (const row of pending) {
    await assertPendingSyncSessionCurrent(snapshot);
    if (!pendingOwnedBy(row, snapshot)) continue;
    try {
      if (!await resumeChecklistMediaAcknowledgement(row, snapshot)) {
        const active = checklistUploadsInFlight.get(row.id);
        if (active && sameChecklistUploadAttempt(active.pending, row)) {
          const current = await databaseService.readItem<PendingImage>('pending_images', row.id);
          const checklist = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME, row.item_id);
          const value = checklist?.empresa_id === snapshot.empresaId && row.campo_id
            ? await findChecklistValueForVisualField(snapshot.empresaId, row.item_id, row.campo_id, checklist.snapshot_id) : null;
          const path = checklistStoragePath(row.local_uri, row.path);
          await assertPendingSyncSessionCurrent(snapshot);
          // A tentativa deve continuar exata após as releituras; outra fila,
          // identidade ou edição do valor não herda o adiamento deste upload.
          if (checklistUploadsInFlight.get(row.id) === active && current && sameChecklistUploadAttempt(current, row) &&
              !checklist?.retired_at && path === row.path &&
              (isChecklistMediaPathInScope(path, row.item_id, snapshot.empresaId) ||
                ((!row.user_id || !row.empresa_id) && isLegacyChecklistMediaPathInCompany(path, snapshot.empresaId))) &&
              value?.empresa_id === snapshot.empresaId && value.checklist_id === row.item_id &&
              (value.valor === row.local_uri || value.valor === current.prepared_public_url)) {
            deferred += 1;
            UploadImageHelperLogger.Main.info(`ACK adiado durante upload da tentativa exata: ${row.id}`);
            continue;
          }
        }
        if (await checklistMediaAwaitingPreparation(row, snapshot)) {
          deferred += 1;
          UploadImageHelperLogger.Main.info(`ACK adiado até a preparação da tentativa exata: ${row.id}`);
          continue;
        }
        errors += 1;
        UploadImageHelperLogger.Main.warn(`ACK de mídia ainda pendente: ${row.id}`);
      }
    } catch (error) {
      await assertPendingSyncSessionCurrent(snapshot);
      errors += 1;
      UploadImageHelperLogger.Main.warn(`Falha no ACK de mídia ${row.id}; demais itens continuarão.`, error);
    }
  }
  return { errors, deferred };
}

async function uploadPreparedChecklistMedia(
  pending: PendingImage,
  barriers: Map<string, Set<string>> | undefined,
  suppliedSnapshot?: PendingSyncSessionSnapshot
): Promise<ImageUploadResult> {
  const record = createMediaDiagnosticRecorder({ checklistId: pending.item_id, fieldId: pending.campo_id || '', attemptId: pending.id, mediaRef: pending.local_uri });
  const stale: ImageUploadResult = { publicURL: '', storagePath: pending.path, superseded: true };
  try {
    const snapshot = await resolvePendingSessionSnapshot(suppliedSnapshot);
    if (!snapshot || !pendingOwnedBy(pending, snapshot)) {
      throw new Error('Pendência de mídia sem autoria elegível para a sessão atual.');
    }
    if (await resumeChecklistMediaAcknowledgement(pending, snapshot)) {
      return { publicURL: createPendingSyncSupabaseClient(snapshot).storage.from(pending.bucket)
        .getPublicUrl(checklistStoragePath(pending.local_uri, pending.path)).data.publicUrl, storagePath: pending.path, localPersisted: true, remoteAcknowledged: true };
    }
    const pendingClient = createPendingSyncSupabaseClient(snapshot);
    const current = await databaseService.readItem<PendingImage>('pending_images', pending.id);
    if (!current || current.local_uri !== pending.local_uri || current.item_id !== pending.item_id ||
        current.campo_id !== pending.campo_id || current.user_id !== pending.user_id ||
        current.empresa_id !== pending.empresa_id || !pendingOwnedBy(current, snapshot)) { record('superseded'); return stale; }
    const checklist = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME, pending.item_id);
    const queuedCompanyId = pending.empresa_id === null || pending.empresa_id === undefined || pending.empresa_id === ''
      ? null
      : typeof pending.empresa_id === 'string' ? pending.empresa_id.trim() : false;
    if (!checklist?.empresa_id || checklist.empresa_id !== snapshot.empresaId || queuedCompanyId === false ||
        (queuedCompanyId && queuedCompanyId !== checklist.empresa_id) || !pending.campo_id) {
      throw new Error('Checklist, empresa ou campo não pertence à sessão originadora da mídia.');
    }
    const campoId = pending.campo_id;
    if (!campoId) throw new Error('Campo ausente para retomar mídia pendente.');
    const existingValue = await findChecklistValueForVisualField(checklist.empresa_id, pending.item_id,
      campoId, checklist.snapshot_id);
    const path = checklistStoragePath(pending.local_uri, pending.path);
    const expectedPublicUrl = pendingClient.storage.from(pending.bucket).getPublicUrl(path).data?.publicUrl;
    const pendingCompany = typeof pending.empresa_id === 'string' ? pending.empresa_id.trim() : '';
    const legacyCompanyPath = (!pending.user_id || !pendingCompany) &&
      isLegacyChecklistMediaPathInCompany(path, snapshot.empresaId);
    const alreadyPrepared = Boolean(expectedPublicUrl && existingValue?.valor === expectedPublicUrl);
    if (!alreadyPrepared && existingValue?.valor !== pending.local_uri) { record('superseded'); return stale; }
    if (alreadyPrepared) {
      if (path !== pending.path ||
          (!isChecklistMediaPathInScope(path, pending.item_id, snapshot.empresaId) && !legacyCompanyPath)) {
        throw new Error('Caminho da mídia pendente diverge do checklist/empresa.');
      }
      const snapshotField = await findChecklistSnapshotField(snapshot.empresaId, checklist.snapshot_id, campoId);
      const identity = resolveChecklistValueIdentity(campoId, snapshotField);
      const linkedValues = await databaseService.listItems<ChecklistValueRecord>(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME, { checklist_id: pending.item_id }
      );
      const exactLinks = linkedValues.filter(row => row.empresa_id === snapshot.empresaId &&
        matchesChecklistValueIdentity(row, identity, campoId));
      const operations = await databaseService.getPendingOperations();
      const exactPartials = operations.filter(op => {
        if (op.operation !== 'checklist_partial_update' ||
            op.collection !== APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME ||
            (op.itemId ?? (op as any).item_id) !== pending.item_id ||
            op.user_id !== pending.user_id || op.empresa_id !== pending.empresa_id) return false;
        try {
          const data = typeof op.data === 'string' ? JSON.parse(op.data) : op.data;
          if (!data || Array.isArray(data)) return false;
          const answer = data[campoId];
          return checklistAnswerValue(answer) === expectedPublicUrl ||
            (hasAnswerObservation(answer) && !Object.prototype.hasOwnProperty.call(answer, 'valor') &&
              answer.observacao === (existingValue?.observacao ?? null));
        } catch { return false; }
      });
      if (exactLinks.length !== 1 || exactLinks[0].id !== existingValue?.id ||
          exactPartials.length !== 1) {
        throw new Error('Referência de mídia preparada sem pendência parcial exata; fila preservada.');
      }
    }
    // syncService também importa este helper; resolver somente durante o envio evita inicialização circular.
    const resolvedBarriers = barriers ?? await (require('@/services/syncService') as typeof import('@/services/syncService')).runTombstonePreflightForRemoteWrites();
    const ancestry = await resolveChecklistMediaAncestry(pending.item_id, pending.campo_id);
    if (hasChecklistMediaTombstone(resolvedBarriers, ancestry)) {
      record('tombstone_blocked');
      await assertPendingSyncSessionCurrent(snapshot);
      await databaseService.retainTombstonedChecklistUpload(pending, snapshot.empresaId, pendingSessionStateGuard(snapshot));
      const value = await findChecklistValueForVisualField(checklist.empresa_id, pending.item_id,
        pending.campo_id, checklist.snapshot_id);
      return value?.valor === pending.local_uri
        ? { publicURL: pending.local_uri, storagePath: pending.path, localPersisted: true, remoteAcknowledged: false } : stale;
    }
    await assertChecklistNotCancelled(snapshot.empresaId, pending.item_id);
    record('upload_started');
    if (alreadyPrepared) {
      await assertPendingSyncSessionCurrent(snapshot);
      const { data: stored, error: storedError } = await withNetworkTimeout(
        pendingClient.storage.from(pending.bucket).info(path)
      );
      await assertPendingSyncSessionCurrent(snapshot);
      if (stored && !storedError) {
        // URL e outbox já foram confirmadas numa transação local anterior.
        // Falta ao push criar/confirmar a resposta remota para permitir o ACK.
        return { publicURL: expectedPublicUrl!, storagePath: path, localPersisted: true, remoteAcknowledged: false };
      }
      if (storedError && !isStorageObjectMissingError(storedError)) throw storedError;
    }
    const relocatedSource = await resolveIOSChecklistMediaSource(pending, snapshot.empresaId);
    if (relocatedSource && (!pending.user_id?.trim() || pending.empresa_id !== snapshot.empresaId)) {
      record('local_source_relocation_blocked');
      throw new Error('Arquivo localizado sem autoria exata para esta sessão; fila preservada.');
    }
    const fileData = relocatedSource
      ? await readRelocatedIOSChecklistMediaSource(relocatedSource)
      : await fetchFile(pending.local_uri);
    if (relocatedSource) {
      await assertPendingSyncSessionCurrent(snapshot);
      const latest = await databaseService.readItem<PendingImage>('pending_images', pending.id);
      if (!latest || !sameChecklistUploadAttempt(latest, pending)) { record('superseded'); return stale; }
      record('local_source_relocated');
    }
    // O nome local é único por captura; repetir o mesmo path retoma um upload
    // interrompido entre o Storage e a confirmação no banco local.
    const extension = pending.local_uri.split('.').pop()?.toLowerCase();
    const contentType = extension === 'png' ? 'image/png' : extension === 'webp' ? 'image/webp' : 'image/jpeg';
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      await assertPendingSyncSessionCurrent(snapshot);
      await assertChecklistNotCancelled(snapshot.empresaId, pending.item_id);
      if (!isPendingSyncSessionSnapshotStateCurrent(snapshot)) {
        throw new Error('Sessão mudou antes do envio; mídia pendente preservada.');
      }
      const { error } = await Promise.race([
        pendingClient.storage.from(pending.bucket).upload(path, fileData, { upsert: false, contentType }),
        new Promise<never>((_, reject) => { timeout = setTimeout(() => reject(new Error('Tempo de envio da imagem excedido.')), NETWORK_TIMEOUT); }),
      ]);
      if (error && !isResourceAlreadyExistsError(error)) throw error;
    } finally { if (timeout) clearTimeout(timeout); }
    record('storage_uploaded');
    await assertPendingSyncSessionCurrent(snapshot);
    const { data: objectInfo, error: objectInfoError } = await withNetworkTimeout(
      pendingClient.storage.from(pending.bucket).info(path)
    );
    if (objectInfoError || !objectInfo) {
      throw objectInfoError ?? new Error('Storage não confirmou a presença do objeto enviado.');
    }
    await assertPendingSyncSessionCurrent(snapshot);
    const publicURL = pendingClient.storage.from(pending.bucket).getPublicUrl(path).data?.publicUrl;
    if (!publicURL) throw new Error('Falha ao obter URL pública da imagem');
    if (alreadyPrepared) {
      if (publicURL !== expectedPublicUrl) throw new Error('URL preparada diverge do objeto confirmado.');
      // A transação local anterior já gravou URL e outbox. O push fará o
      // readback remoto antes de limpar a mídia no final deste ciclo.
      return { publicURL, storagePath: path, localPersisted: true, remoteAcknowledged: false };
    }
    await assertPendingSyncSessionCurrent(snapshot);
    const committed = await databaseService.completeChecklistMedia({
      empresaId: checklist.empresa_id, checklistId: pending.item_id, campoId: pending.campo_id,
      pendingId: pending.id, localUri: pending.local_uri, publicUrl: publicURL,
      userId: pending.user_id ?? null, pendingEmpresaId: pending.empresa_id ?? null,
      canAcknowledge: pendingSessionStateGuard(snapshot),
    });
    if (!committed) {
      if (!isPendingSyncSessionSnapshotStateCurrent(snapshot)) {
        throw new Error('Sessão mudou antes do ACK da mídia; fila e arquivo preservados.');
      }
      record('superseded');
      return stale;
    }
    record('reference_committed');
    // A referência local está pronta para o push; fila e bytes aguardam o ACK remoto.
    return { publicURL, storagePath: path, localPersisted: true, remoteAcknowledged: false };
  } catch (error) {
    record('upload_failed', error);
    // A fila foi preparada antes da rede. Nenhum fallback pode sobrescrever
    // uma edição nova, e a falha original é registrada antes de qualquer releitura.
    captureSentryExceptionWithScope(error, {
      context: { flow: 'upload_image_helper', stage: 'prepared_checklist', itemId: pending.item_id,
        campoId: pending.campo_id, pendingId: pending.id },
      tags: { flow: 'upload', stage: 'prepared_checklist', item_type: 'CHECKLIST' },
    });
    throw error;
  }
}

export async function uploadImageHelper(params: UploadImageParams): Promise<ImageUploadResult> {
  if (isStorageMediaReference(params.localUri)) throw new Error('Mídia privada remota não pode virar upload local.');
  if (params.itemType !== 'CHECKLIST') {
    let pendingQueueOrigin = params.pendingQueueOrigin;
    if (params.pendingImageId && pendingQueueOrigin === undefined) {
      const queued = await databaseService.readItem<PendingImage>('pending_images', params.pendingImageId);
      if (!queued || queued.local_uri !== params.localUri || queued.item_id !== params.itemId ||
          queued.item_type !== params.itemType) {
        return { publicURL: '', storagePath: '', superseded: true };
      }
      pendingQueueOrigin = queued.user_id && queued.empresa_id
        ? { userId: queued.user_id, empresaId: queued.empresa_id }
        : null;
    }
    return uploadLegacyImageHelper({ ...params, pendingQueueOrigin });
  }
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) throw new Error('Upload bloqueado pelo modo de recuperacao.');
  let pending: PendingImage | null;
  if (params.pendingImageId) {
    try {
      pending = await databaseService.readItem<PendingImage>('pending_images', params.pendingImageId);
    } catch (error) {
      createMediaDiagnosticRecorder({ checklistId: params.itemId, fieldId: params.campoId || '',
        attemptId: params.pendingImageId })('upload_failed', error);
      captureSentryExceptionWithScope(error, {
        context: { flow: 'upload_image_helper', stage: 'read_pending', itemId: params.itemId,
          campoId: params.campoId, pendingId: params.pendingImageId },
        tags: { flow: 'upload', stage: 'read_pending', item_type: 'CHECKLIST' },
      });
      throw error;
    }
  } else {
    pending = await prepareChecklistMediaUpload(params.itemId, params.campoId || '', params.localUri);
  }
  if (!pending || pending.item_type !== 'CHECKLIST' || pending.item_id !== params.itemId ||
      pending.campo_id !== params.campoId || pending.local_uri !== params.localUri) {
    return { publicURL: '', storagePath: '', superseded: true };
  }
  params.onLocalPersisted?.(params.localUri);
  const running = checklistUploadsInFlight.get(pending.id);
  if (running && sameChecklistUploadAttempt(running.pending, pending)) return running.operation;
  const operation = uploadPreparedChecklistMedia(pending, params.tombstoneBarriers, params.pendingSessionSnapshot);
  const active = { pending: { ...pending }, operation };
  checklistUploadsInFlight.set(pending.id, active);
  try { return await operation; }
  finally { if (checklistUploadsInFlight.get(pending.id) === active) checklistUploadsInFlight.delete(pending.id); }
}

async function uploadLegacyImageHelper({
  localUri,
  itemType,
  itemId,
  columnName,
  campoId,
  previousLocalUri = null,
  previousImageUrl = null,
  pendingSessionSnapshot,
  pendingImageId,
  pendingQueueOrigin,
}: UploadImageParams): Promise<{ publicURL: string; storagePath: string }> {
  const origin = pendingQueueOrigin === undefined ? capturePendingWorkOrigin() : pendingQueueOrigin;
  let sessionSnapshot: PendingSyncSessionSnapshot | null = null;
  try {
    sessionSnapshot = await resolvePendingSessionSnapshot(pendingSessionSnapshot);
  } catch (error) {
    if (pendingImageId) throw error;
  }
  if (pendingImageId && (!origin || !sessionSnapshot ||
      origin.userId !== sessionSnapshot.userId || origin.empresaId !== sessionSnapshot.empresaId)) {
    throw new Error('Sessão atual não pode retomar esta pendência de mídia.');
  }
  if (sessionSnapshot && origin &&
      (origin.userId !== sessionSnapshot.userId || origin.empresaId !== sessionSnapshot.empresaId)) {
    throw new Error('Sessão atual diverge da autoria da operação de mídia.');
  }
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    throw new Error('Upload bloqueado pelo modo de recuperacao.');
  }

  let remoteObjectConfirmed = false;

  const {
    STORAGE: { BUCKET, FOLDER },
  } = APP_CONSTANTS;

  UploadImageHelperLogger.Upload.info(`Iniciando upload de imagem para o item: ${itemId} do tipo: ${itemType} na coluna: ${columnName} e campoId: ${campoId}`);
  addSentryBreadcrumb({
    category: 'upload-image',
    message: 'uploadImageHelper:start',
    level: 'info',
    data: {
      itemType,
      itemId,
      campoId: campoId || null,
      columnName: columnName || null,
    },
  });
  traceImage('upload.start', { itemType, itemId, campoId });
  const uploadKey = buildUploadKey(itemType, itemId, campoId, columnName);
  const currentToken = uuid();
  activeUploadTokens.set(uploadKey, currentToken);
  const clearUploadToken = () => {
    const stored = activeUploadTokens.get(uploadKey);
    if (stored === currentToken) {
      activeUploadTokens.delete(uploadKey);
    }
  };

  // 1. Determina bucket e pasta
  const bucket = itemType === 'USER' ? BUCKET.USER : itemType === 'COMPANY' ? BUCKET.COMPANY : BUCKET.CHECKLIST;
  const folder = itemType === 'USER'
    ? FOLDER.USER_AVATAR
    : itemType === 'COMPANY' ? FOLDER.COMPANY_LOGO : localUri.includes('checklist-photos') ? FOLDER.CHECKLIST_PHOTOS : localUri.includes('checklist-signatures') ? FOLDER.CHECKLIST_SIGNATURES : localUri.includes('checklist-marcacoes') ? FOLDER.CHECKLIST_MARCACOES : '';

  UploadImageHelperLogger.Upload.info(`Bucket: ${bucket}, Folder: ${folder}`);

  await debugPause('UploadImageHelper 2. Excluir imagem local anterior de forma atômica');
  // 2. Excluir imagem local anterior de forma atômica

  const isSameLocalReference = Boolean(previousLocalUri && previousLocalUri === localUri);
  UploadImageHelperLogger.Upload.info('Excluindo imagem local anterior');
  if (previousLocalUri && !isSameLocalReference) {
    UploadImageHelperLogger.Upload.info('Verificando existência da imagem local anterior');
    const exists = await fileExistsAsync(previousLocalUri);
    if (exists) {
      try {
        if (!origin || (sessionSnapshot
          ? !isPendingSyncSessionSnapshotStateCurrent(sessionSnapshot)
          : !samePendingWorkOrigin(origin, capturePendingWorkOrigin()))) {
          throw new Error('Sessão/autoria mudou antes da limpeza do arquivo anterior.');
        }
        await deleteLocalFile(previousLocalUri);
        UploadImageHelperLogger.Delete.info('Imagem local anterior excluída');
      } catch (err) {
        UploadImageHelperLogger.Delete.warn(
          `Falha ao excluir imagem local anterior, pendência mantida: ${err}`
        );

        // Verificando se já existe uma pendência para esta deleção
        const existingPendingDeletions = await databaseService.listItems<PendingDeletion>('pending_deletions', {
          type: 'LOCAL',
          path: previousLocalUri,
          itemType,
          itemId
        });

        UploadImageHelperLogger.Delete.info(`Verificando pendências de deleção existentes: encontradas ${existingPendingDeletions.length}`);
        if (existingPendingDeletions.length > 0) {
          existingPendingDeletions.forEach((pending, idx) => {
            UploadImageHelperLogger.Delete.info(
              `Detalhe da pendência local [${idx}]: id=${pending.id}, type=${pending.type}, itemType=${pending.itemType}, itemId=${pending.itemId}, createdAt=${pending.createdAt}`
            );
          });
        }

        // Só registra nova pendência se não existir
        const sameOriginDeletions = existingPendingDeletions.filter(pending =>
          pending.user_id === (origin?.userId ?? null) && pending.empresa_id === (origin?.empresaId ?? null)
        );
        if (sameOriginDeletions.length === 0) {
          UploadImageHelperLogger.Delete.info(`Nenhuma pendência de deleção existente, registrando nova`);
          await databaseService.createItem('pending_deletions', {
            id: uuid(),
            type: 'LOCAL',
            path: previousLocalUri,
            itemType,
            itemId,
            ...pendingQueueOriginFields(origin),
            createdAt: Date.now(),
          }, true); // skipPendingOperation = true
        } else {
          UploadImageHelperLogger.Delete.info(`Pendência de deleção já existente, não registrando duplicata`);
        }
      }
    } else {
      UploadImageHelperLogger.Delete.info('Arquivo local anterior não existe, pulando exclusão');
    }
  } else if (isSameLocalReference) {
    UploadImageHelperLogger.Delete.info('Valor anterior aponta para o mesmo arquivo em upload, pulando exclusão antecipada.');
  }

  await debugPause('UploadImageHelper 3. Excluir imagem remota anterior se existir');
  // 3. Excluir imagem remota anterior se existir
  UploadImageHelperLogger.Upload.info('Excluindo imagem remota anterior');
  if (previousImageUrl) {
    try {
      UploadImageHelperLogger.Upload.info('Excluindo imagem remota anterior');
      const { bucket: prevBucket, path: prevPath } = extractPathFromUrl(previousImageUrl);
      if (!sessionSnapshot || !origin || sessionSnapshot.userId !== origin.userId ||
          sessionSnapshot.empresaId !== origin.empresaId) {
        throw new Error('Sessão não corresponde à autoria da exclusão de mídia.');
      }
      await removeRemoteStorageObject(createPendingSyncSupabaseClient(sessionSnapshot), prevBucket, prevPath, sessionSnapshot);
      UploadImageHelperLogger.Upload.info(`Imagem remota anterior excluída com sucesso`);
    } catch (err) {
      UploadImageHelperLogger.Upload.warn(`Falha ao excluir imagem remota anterior, registrando pendência: ${err}`);
      try {
        const { path: prevPath } = extractPathFromUrl(previousImageUrl);
        await upsertPendingRemoteDeletion({ path: prevPath, itemType, itemId, campoId }, origin);
        UploadImageHelperLogger.Upload.info('Pendência de deleção remota registrada ou atualizada sem duplicata');
      } catch (extractErr) {
        UploadImageHelperLogger.Upload.error(`Falha ao extrair path da URL anterior: ${extractErr}`);
      }
    }
  }

  await debugPause('UploadImageHelper 4. Gera path único. itemType: ' + itemType);
  // 4. Gera path único
  let filename: string;
  if (itemType === 'CHECKLIST') {
    UploadImageHelperLogger.Upload.info('[CHECKLIST] referencia local preparada para upload');
    // Para fotos de checklist, usar o nome já único do localUri
    // filename = localUri.split('/').pop()!;
    // Remove o prefixo 'checklist-photos/' do localUri para obter o path relativo completo
    //filename = localUri.startsWith('checklist-photos/') ? localUri.replace('checklist-photos/', '') : localUri.split('/').pop()!;
    if (localUri.startsWith('checklist-photos/')) {
      filename = localUri.replace('checklist-photos/', '');
    } else if (localUri.startsWith('checklist-signatures/')) {
      filename = localUri.replace('checklist-signatures/', '');
    } else if (localUri.startsWith('checklist-marcacoes/')) {
      filename = localUri.replace('checklist-marcacoes/', '');
    } else if (localUri.includes('/checklist-photos/')) {
      // Mobile: file:///path/to/files/checklist-photos/empresaId/file.png
      await debugPause('UploadImageHelper [CHECKLIST] mobile checklist-photos');
      const match = localUri.match(/.*\/checklist-photos\/(.+)$/);
      filename = match ? match[1] : localUri.split('/').pop()!;
    } else if (localUri.includes('/checklist-signatures/')) {
      // Mobile: file:///path/to/files/checklist-signatures/empresaId/file.png
      await debugPause('UploadImageHelper [CHECKLIST] mobile checklist-signatures');
      const match = localUri.match(/.*\/checklist-signatures\/(.+)$/);
      filename = match ? match[1] : localUri.split('/').pop()!;
    } else if (localUri.includes('/checklist-marcacoes/')) {
      // Mobile: file:///path/to/files/checklist-marcacoes/empresaId/file.png
      await debugPause('UploadImageHelper [CHECKLIST] mobile checklist-marcacoes');
      const match = localUri.match(/.*\/checklist-marcacoes\/(.+)$/);
      filename = match ? match[1] : localUri.split('/').pop()!;
    } else {
      filename = localUri.split('/').pop()!;
    }
    UploadImageHelperLogger.Upload.info(`[CHECKLIST] filename final para upload: ${filename}`);
    await debugPause('UploadImageHelper [CHECKLIST] filename final para upload: ' + filename);
  } else {
    // USER ou COMPANY
    if (localUri.startsWith('data:image/')) {
      // Extrair o mime type para determinar extensão
      const mime = localUri.substring(5, localUri.indexOf(';')); // e.g. image/jpeg
      let ext = 'jpg';
      if (mime === 'image/png') ext = 'png';
      else if (mime === 'image/gif') ext = 'gif';
      else if (mime === 'image/webp') ext = 'webp';
      filename = `${itemId}/${Date.now()}-${uuid()}.${ext}`;
    } else if (localUri.startsWith('blob:') || localUri.startsWith('temp://')) {
      // Não temos extensão clara, usar jpg por padrão
      filename = `${itemId}/${Date.now()}-${uuid()}.jpg`;
    } else {
      // Caminho de arquivo normal (mobile)
      filename = `${itemId}/${Date.now()}-${localUri.split('/').pop()}`;
    }
  }
  const path = `${folder}/${filename}`;
  UploadImageHelperLogger.Upload.info(`Path de storage gerado para filename=${filename}`);

  // 5. le arquivo local e sobe para o supabase
  try {
    const snapshot = sessionSnapshot;
    if (!snapshot || snapshot.userId !== origin?.userId || snapshot.empresaId !== origin?.empresaId) {
      throw new Error('Sessão não corresponde à autoria da mídia pendente.');
    }
    if ((itemType === 'USER' && itemId !== snapshot.userId) ||
        (itemType === 'COMPANY' && itemId !== snapshot.empresaId)) {
      throw new Error('Imagem fora do escopo do usuário/empresa da sessão atual.');
    }
    const pendingClient = createPendingSyncSupabaseClient(snapshot);
    // Lê arquivo local (Blob ou ArrayBuffer)
    const fileData = await fetchFile(localUri);
    UploadImageHelperLogger.Upload.info('Arquivo local lido para upload');

    // 6. Realiza o upload ao Supabase
    UploadImageHelperLogger.Upload.info(`realizando o upload ao Supabase`);

    // Determinar o contentType baseado na extensão do arquivo
    const fileExtension = localUri.split('.').pop()?.toLowerCase() || '';
    let contentType = 'image/jpeg'; // Padrão para JPEGs

    if (fileExtension === 'png') {
      contentType = 'image/png';
    } else if (fileExtension === 'gif') {
      contentType = 'image/gif';
    } else if (fileExtension === 'webp') {
      contentType = 'image/webp';
    } else if (fileExtension === 'svg') {
      contentType = 'image/svg+xml';
    }

    UploadImageHelperLogger.Upload.info(`contentType determinado: ${contentType} baseado na extensão: ${fileExtension}`);

    await debugPause('UploadImageHelper 6. Faz upload supabase com timeout');
    // Faz upload com timeout
    await assertPendingSyncSessionCurrent(snapshot);
    if (!isPendingSyncSessionSnapshotStateCurrent(snapshot)) {
      throw new Error('Sessão mudou antes do upload; mídia pendente preservada.');
    }
    const { error: uploadError } = await withNetworkTimeout(
      pendingClient.storage.from(bucket).upload(path, fileData, {
        upsert: false,
        contentType
      })
    );
    if (uploadError) {
      if (isResourceAlreadyExistsError(uploadError)) {
        UploadImageHelperLogger.Upload.warn('Upload supabase retornou recurso já existente, reutilizando path');
      } else {
        throw uploadError;
      }
    } else {
      UploadImageHelperLogger.Upload.info(`upload realizado com sucesso`);
    }
    await assertPendingSyncSessionCurrent(snapshot);
    if (!isPendingSyncSessionSnapshotStateCurrent(snapshot)) {
      throw new Error('Sessão mudou antes da confirmação do Storage; mídia pendente preservada.');
    }
    const { data: objectInfo, error: objectInfoError } = await withNetworkTimeout(
      pendingClient.storage.from(bucket).info(path)
    );
    if (objectInfoError || !objectInfo) {
      throw objectInfoError ?? new Error('Storage não confirmou a presença do objeto enviado.');
    }
    remoteObjectConfirmed = true;
    await assertPendingSyncSessionCurrent(snapshot);

    // 7. Recupera URL pública
    UploadImageHelperLogger.Upload.info(`recuperando a URL pública`);
    const { data: publicUrlData } = pendingClient
      .storage
      .from(bucket)
      .getPublicUrl(path);
    UploadImageHelperLogger.Upload.info('recuperou a URL pública');
    const publicURL = publicUrlData?.publicUrl || '';
    UploadImageHelperLogger.Upload.info('URL pública validada');
    if (!publicURL) {
      throw new Error('Falha ao obter URL pública da imagem');
    }
    await assertPendingSyncSessionCurrent(snapshot);

    await debugPause('UploadImageHelper 7. recupera a URL pública');

    // 8. Atualiza a tabela local com a URL pública e remove o caminho local
    // Se for CHECKLIST, salva valor na tabela de valores do checklist
    if (itemType === 'CHECKLIST' && campoId) {
      const isLatestUpload = activeUploadTokens.get(uploadKey) === currentToken;
      if (!isLatestUpload) {
        UploadImageHelperLogger.Upload.info(
          '[UploadImageHelper] Upload concluído para checklist, porém existe tentativa mais recente. Ignorando persistência.'
        );
      } else {
        await assertPendingSyncSessionCurrent(snapshot);
        const checklist = await databaseService.readItem<any>(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
          itemId
        );
        if (!checklist || !checklist.empresa_id) {
          throw new Error('Checklist ou empresa_id não encontrado para o checklist_id: ' + itemId);
        }
        const existingRecords = await databaseService.listItems<ChecklistValueRecord>(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
          { checklist_id: itemId }
        );
        const snapshotField = await findChecklistSnapshotField(
          checklist.empresa_id,
          checklist.snapshot_id,
          campoId
        );
        const identity = resolveChecklistValueIdentity(campoId, snapshotField);
        const existingRecord = findChecklistValueByIdentity(existingRecords, identity, campoId);
        const preferCreate = !existingRecord || !isRemoteChecklistValue(existingRecord.valor);
        const mode: ChecklistPersistMode = preferCreate ? 'create' : 'update';
        await persistChecklistValue({
          empresaId: checklist.empresa_id,
          checklistId: itemId,
          campoId,
          valor: publicURL,
          mode,
          existing: existingRecord,
          snapshotId: checklist.snapshot_id,
          snapshotField,
          pendingOrigin: origin,
          canAcknowledge: pendingSessionStateGuard(snapshot),
        });
        traceImage('upload.success', { itemType, itemId, campoId, to: 'remote' });
        UploadImageHelperLogger.Upload.info(
          `Valor do campo de foto atualizado na tabela guincho_valores_checklist (modo=${mode})`
        );
      }
    } else if ((itemType === 'USER' || itemType === 'COMPANY') && columnName) {
      // USER/COMPANY: Atualiza a tabela local com a URL pública e remove o caminho local
      const tableName = itemType === 'USER'
        ? APP_CONSTANTS.DATABASE.TABLES.USERS.NAME
        : APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME;
      UploadImageHelperLogger.Upload.info(`tableName: ${tableName}`);
      await debugPause('UploadImageHelper 8. USER/COMPANY: Atualiza a tabela local com a URL pública e remove o caminho local');
      await assertPendingSyncSessionCurrent(snapshot);
      if (!isPendingSyncSessionSnapshotStateCurrent(snapshot)) {
        throw new Error('Sessão mudou antes de atualizar a referência local; fila preservada.');
      }
      const referenceUpdated = await updateImageRowIfCurrent(
        tableName,
        itemId,
        {
          [columnName]: publicURL,
          [`${columnName.replace('_url', '')}_local_path`]: null
        },
        origin,
        snapshot,
        false,
      );
      if (!referenceUpdated) throw new Error('Referência da mídia mudou ou a sessão trocou antes do commit local.');
      UploadImageHelperLogger.Upload.info(`atualizou a tabela local com sucesso`);
    }

    await debugPause('UploadImageHelper 9. Remove arquivo local temporário de forma atômica');
    // 9. Remove arquivo local temporário de forma atômica
    UploadImageHelperLogger.Upload.info(`removendo o arquivo local temporário`);
    try {
      const existsTemp = await fileExistsAsync(localUri);
      await debugPause('UploadImageHelper 9. Remove arquivo local temporário de forma atômica - verifica se o arquivo existe = ' + existsTemp);
      if (existsTemp) {
        await assertPendingSyncSessionCurrent(snapshot);
        if (!isPendingSyncSessionSnapshotStateCurrent(snapshot)) {
          throw new Error('Sessão mudou antes de limpar o arquivo local; fila preservada.');
        }
        await debugPause('UploadImageHelper 9. Remove arquivo local temporário de forma atômica');
        await deleteLocalFile(localUri);
        UploadImageHelperLogger.Upload.info('Arquivo temporário excluído');
      } else {
        UploadImageHelperLogger.Upload.info('Arquivo temporário já não existe, pulando');
      }
    } catch (err) {
      UploadImageHelperLogger.Upload.warn(`Erro ao excluir arquivo temporário, continuando: ${err}`);
    }

    await debugPause('UploadImageHelper 10. Remove pendência se existir');
    // 10. Remove pendência se existir
    UploadImageHelperLogger.Upload.info(`removendo a pendência`);
    const pendingQuery: any = {};
    if (itemId) pendingQuery.item_id = itemId;
    if (itemType) pendingQuery.item_type = itemType;
    if (columnName !== undefined) pendingQuery.column_name = columnName;
    if (campoId !== undefined) pendingQuery.campo_id = campoId;
    const pendingImages = await databaseService.listItems<PendingImage>('pending_images', pendingQuery);
    UploadImageHelperLogger.Upload.info(`encontrei ${pendingImages.length} pendências`);
    await debugPause('UploadImageHelper 10. Remove pendência se existir - lista pendências = ' + pendingImages.length);
    for (const pendingImage of pendingImages.filter(pending => pendingOwnedBy(pending, snapshot))) {
      UploadImageHelperLogger.Upload.info(`removendo a pendência: ${pendingImage.id}`);
      await assertPendingSyncSessionCurrent(snapshot);
      if (!isPendingSyncSessionSnapshotStateCurrent(snapshot)) {
        throw new Error('Sessão mudou antes do ACK da fila de mídia.');
      }
      await databaseService.deleteItemIfUnchanged('pending_images', pendingImage.id, {
        item_type: pendingImage.item_type,
        item_id: pendingImage.item_id,
        column_name: pendingImage.column_name ?? null,
        campo_id: pendingImage.campo_id ?? null,
        local_uri: pendingImage.local_uri,
        user_id: snapshot.userId,
        empresa_id: snapshot.empresaId,
      }, true, pendingSessionStateGuard(snapshot));
      UploadImageHelperLogger.Upload.info(`pendência removida com sucesso: ${pendingImage.id}`);
    }
    UploadImageHelperLogger.Upload.info('Upload da imagem concluído com sucesso');
    addSentryBreadcrumb({
      category: 'upload-image',
      message: 'uploadImageHelper:success',
      level: 'info',
      data: {
        itemType,
        itemId,
        campoId: campoId || null,
        storagePath: path,
      },
    });

    await debugPause('UploadImageHelper 11. Retorna a URL pública e o path no storage');
    // Retorna a URL pública e o path no storage
    return { publicURL, storagePath: path };
  } catch (err) {
    await debugPause('UploadImageHelper 12. Upload falhou, registrando pendência');
    console.warn('Upload falhou, registrando pendência', err);
    traceImage('upload.fail', { itemType, itemId, campoId, error: (err as any)?.message });
    if (pendingImageId && !isPendingWorkOriginCurrent(origin)) {
      // A fila é retomada pela identidade original, sem reescrevê-la após a troca.
      throw err;
    }
    // Fallback offline: se CHECKLIST, grava valor localUri
    if (itemType === 'CHECKLIST' && campoId) {
      const isLatestUpload = activeUploadTokens.get(uploadKey) === currentToken;
      if (!isLatestUpload) {
        UploadImageHelperLogger.Upload.info(
          '[UploadImageHelper] Falha detectada em upload substituído. Ignorando persistência offline.'
        );
      } else {
        const checklist = await databaseService.readItem<any>(
          APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
          itemId
        );
        if (!checklist || !checklist.empresa_id) {
          throw new Error('Checklist ou empresa_id não encontrado para o checklist_id: ' + itemId);
        }
        UploadImageHelperLogger.Upload.info(
          'UploadImageHelper 12. CHECKLIST: Upload falhou, salvando valor localUri em guincho_valores_checklist (modo=skip).'
        );
        await debugPause(
          'UploadImageHelper 12. CHECKLIST: Upload falhou, salvando valor localUri em guincho_valores_checklist (persistChecklistValue skip)'
        );
        await persistChecklistValue({
          empresaId: checklist.empresa_id,
          checklistId: itemId,
          campoId,
          valor: localUri,
          mode: 'skip',
          snapshotId: checklist.snapshot_id,
          pendingOrigin: origin,
          canAcknowledge: sessionSnapshot
            ? pendingSessionStateGuard(sessionSnapshot)
            : () => isPendingWorkOriginCurrent(origin),
        });
        traceImage('upload.fallback.localSaved', { itemType, itemId, campoId, to: 'local' });
        UploadImageHelperLogger.Upload.info(
          `UploadImageHelper Valor local da foto salvo offline em guincho_valores_checklist`
        );
      }
    } else if ((itemType === 'USER' || itemType === 'COMPANY') && columnName) {
      // USER/COMPANY: Atualiza o campo local_path na tabela principal, protegendo com try/catch
      const tableName = itemType === 'USER'
        ? APP_CONSTANTS.DATABASE.TABLES.USERS.NAME
        : APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME;
      const localPathColumn = `${columnName.replace('_url', '')}_local_path`;
      try {
        if (!remoteObjectConfirmed) {
          await updateImageRowIfCurrent(
            tableName,
            itemId,
            { [localPathColumn]: localUri },
            origin,
            sessionSnapshot,
            true // skipPendingOperation = true
          );
        }
      } catch (e) {
        UploadImageHelperLogger.Upload.warn(`Coluna localPathColumn não existe ou erro ao atualizar: ${e}`);
      }
    }
    // Registrar pendência
    const pendingQuery: any = {};
    if (itemId) pendingQuery.item_id = itemId;
    if (itemType) pendingQuery.item_type = itemType;
    if (columnName !== undefined) pendingQuery.column_name = columnName;
    if (campoId !== undefined) pendingQuery.campo_id = campoId;
    const existingPendingImages = await databaseService.listItems<PendingImage>('pending_images', pendingQuery);
    UploadImageHelperLogger.Upload.info(`Verificando pendências existentes antes de registrar nova: encontradas ${existingPendingImages.length}`);
    await debugPause(`Verificando pendências existentes antes de registrar nova: encontradas ${existingPendingImages.length}`);
    const sameOriginPendingImages = existingPendingImages.filter(pending =>
      pending.user_id === (origin?.userId ?? null) && pending.empresa_id === (origin?.empresaId ?? null)
    );
    if (sameOriginPendingImages.length > 0) {
      existingPendingImages.forEach((pending, idx) => {
        UploadImageHelperLogger.Upload.info(
          `Detalhe da pendência [${idx}]: id=${pending.id}, item_id=${pending.item_id}, item_type=${pending.item_type}, column_name=${pending.column_name}, bucket=${pending.bucket}, created_at=${pending.created_at}`
        );
      });
    }
    // Só registra nova pendência se não existir
    if (sameOriginPendingImages.length === 0) {
      UploadImageHelperLogger.Upload.info(`Nenhuma pendência existente, registrando nova pendência`);
      await debugPause('UploadImageHelper 12. Upload falhou, registrando pendência');
      await databaseService.createItem('pending_images', {
        id: uuid(),
        item_type: itemType,
        item_id: itemId,
        local_uri: localUri,
        bucket,
        path,
        column_name: columnName,
        campo_id: campoId,
        ...pendingQueueOriginFields(origin),
        created_at: Date.now(),
      }, true); // skipPendingOperation = true
      traceImage('pendingImages.create', { itemType, itemId, campoId });
    } else {
      UploadImageHelperLogger.Upload.info(`Pendência já existente, atualizando com novos dados`);
      const existingPending = sameOriginPendingImages[0];
      await databaseService.updateItem('pending_images', existingPending.id, {
        local_uri: localUri,
        bucket,
        path,
        column_name: columnName,
        campo_id: campoId,
        created_at: Date.now(),
      }, true); // skipPendingOperation = true
      UploadImageHelperLogger.Upload.info(`Pendência atualizada com sucesso para bucket=${bucket}, column_name=${columnName}, campo_id=${campoId}`);
    }
    captureSentryExceptionWithScope(err, {
      context: {
        flow: 'upload_image_helper',
        stage: 'upload',
        itemType,
        itemId,
        campoId: campoId || null,
        columnName: columnName || null,
        localUri: summarizeUriForTelemetry(localUri),
        previousImageUrl: summarizeUriForTelemetry(previousImageUrl),
        bucket,
      },
      tags: {
        flow: 'upload',
        stage: 'upload_image_helper',
        item_type: itemType,
      },
      fingerprint: ['upload', 'upload_image_helper_exception', itemType],
      dedupeKey: buildUploadFailureDedupeKey({
        itemType,
        itemId,
        campoId,
        columnName,
        error: err,
      }),
      dedupeWindowMs: 30000,
    });
    throw err;
  } finally {
    clearUploadToken();
  }
}

/**
 * Processa imagens pendentes durante a sincronização
 * Esta função deve ser chamada pelo serviço de sincronização
 */
export async function processPendingImages(options: PendingProcessOptions = {}): Promise<PendingProcessResult> {
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    UploadImageHelperLogger.Main.warn('[RECOVERY] Processamento de imagens pendentes bloqueado');
    return { total: 0, processed: 0, success: 0, errors: 0 };
  }

  let snapshot: PendingSyncSessionSnapshot | null;
  try {
    snapshot = await resolvePendingSessionSnapshot(options.sessionSnapshot);
  } catch (error) {
    UploadImageHelperLogger.Main.info('Sessão mudou antes do processamento das imagens pendentes; fila preservada.', error);
    return { total: 0, processed: 0, success: 0, errors: 0 };
  }
  if (!snapshot) {
    UploadImageHelperLogger.Main.info('Usuário não autenticado, não processando imagens pendentes');
    return {
      total: 0,
      processed: 0,
      success: 0,
      errors: 0,
    };
  }

  UploadImageHelperLogger.Main.info('Processando imagens pendentes...');

  // Busca todas as pendências
  const allPendings = await databaseService.listItems<PendingImage>('pending_images');
  const pendings = allPendings.filter(pending =>
    pendingOwnedBy(pending, snapshot!) &&
    (!options.allowedPendingIds || options.allowedPendingIds.has(pending.id))
  );

  UploadImageHelperLogger.Main.info(`Encontradas ${pendings.length} imagens pendentes para upload`);

  let processed = 0;
  let success = 0;
  let errors = 0;

  if (pendings.length === 0) {
    options.onProgress?.({
      context: 'images',
      processed,
      success,
      errors,
      total: pendings.length,
    });
    return {
      total: pendings.length,
      processed,
      success,
      errors,
    };
  }

  //await debugPause('check 1');
  // Processa cada pendência
  let sessionChanged = false;
  for (const p of pendings) {
    //await debugPause('check 2');
    try {
      await assertPendingSyncSessionCurrent(snapshot);
      await assertPendingChecklistUploadIsSafe(p, options.tombstoneBarriers, snapshot);
      if (await resumeChecklistMediaAcknowledgement(p, snapshot)) {
        success += 1;
        continue;
      }
      const shouldValidateLocalSource = Boolean(p.local_uri) && !isRemoteHttpUri(p.local_uri);
      if (shouldValidateLocalSource) {
        let localExists = await fileExistsAsync(p.local_uri);
        if (!localExists && p.item_type === 'CHECKLIST') {
          const located = await resolveIOSChecklistMediaSource(p, snapshot.empresaId);
          if (located && (!p.user_id?.trim() || p.empresa_id !== snapshot.empresaId)) {
            createMediaDiagnosticRecorder({ checklistId: p.item_id, fieldId: p.campo_id || '',
              attemptId: p.id, mediaRef: p.local_uri })('local_source_relocation_blocked');
            // Encontrar o arquivo não atribui autoria à fila legada (por exemplo, checklist287).
            errors += 1;
            continue;
          }
          localExists = Boolean(located);
        }
        if (!localExists) {
          if (p.item_type === 'CHECKLIST') {
            UploadImageHelperLogger.Main.warn(
              `Arquivo de mídia do checklist ausente sem ACK remoto exato; mantendo fila: ${p.id}`
            );
            errors += 1;
            continue;
          }
          await assertPendingSyncSessionCurrent(snapshot);
          await discardDeadPendingImage(p, 'local_source_missing', snapshot);
          success += 1;
          continue;
        }
      }

      const uploadResult = await uploadImageHelper({
        localUri: p.local_uri,
        itemType: p.item_type,
        itemId: p.item_id,
        pendingImageId: p.id,
        pendingSessionSnapshot: snapshot,
        pendingQueueOrigin: { userId: p.user_id!, empresaId: p.empresa_id! },
        tombstoneBarriers: options.tombstoneBarriers,
        ...(p.column_name ? { columnName: p.column_name } : {}),
        ...(p.campo_id ? { campoId: p.campo_id } : {}),
      });
      // Uma tentativa substituída ou apenas preparada não prova entrega remota.
      if (uploadResult.superseded || (p.item_type === 'CHECKLIST' && !uploadResult.remoteAcknowledged)) {
        UploadImageHelperLogger.Main.info(`Imagem pendente sem confirmação remota: ${p.id}`);
      } else {
        UploadImageHelperLogger.Main.info(`Imagem pendente confirmada: ${p.id}`);
        success += 1;
      }
    } catch (error) {
      if (!await isPendingSyncSessionCurrent(snapshot)) {
        sessionChanged = true;
        UploadImageHelperLogger.Main.info(
          `[processPendingImages] Sessão mudou durante a tentativa ${p.id}; fila e arquivo mantidos.`,
        );
      }
      if (sessionChanged) {
        errors += 1;
      } else {
      if (isMissingLocalResourceError(error) && p.item_type !== 'CHECKLIST') {
        UploadImageHelperLogger.Main.warn(
          `[processPendingImages] Erro definitivo de arquivo local ausente para pendência ${p.id}; descartando.`,
          error
        );
        try {
          await assertPendingSyncSessionCurrent(snapshot);
          await discardDeadPendingImage(p, 'local_source_missing_after_error', snapshot);
          success += 1;
          continue;
        } catch (cleanupError) {
          UploadImageHelperLogger.Main.warn(
            `[processPendingImages] Falha ao descartar pendência morta ${p.id}: ${cleanupError}`
          );
        }
      }

      UploadImageHelperLogger.Main.warn(`Falha ao processar imagem pendente: ${p.id}`, error);
      // Mantém a pendência para tentar novamente mais tarde
      errors += 1;
      }
    } finally {
      processed += 1;
      options.onProgress?.({
        context: 'images',
        processed,
        success,
        errors,
        total: pendings.length,
      });
    }
    if (sessionChanged) break;
  }

  if (errors > 0) {
    captureSentryMessageWithScope('Processamento de imagens pendentes com falhas', {
      level: 'warning',
      context: {
        flow: 'pending_images',
        total: pendings.length,
        processed,
        success,
        errors,
      },
      tags: {
        flow: 'sync',
        stage: 'pending_images',
        result: 'partial',
      },
      fingerprint: ['sync', 'pending_images_partial'],
      dedupeKey: 'sync:pending_images_partial',
      dedupeWindowMs: 60000,
    });
  }

  return {
    total: pendings.length,
    processed,
    success,
    errors,
  };
}

/**
 * Processa pendências de deleção durante a sincronização
 * Esta função deve ser chamada pelo serviço de sincronização
 */
export async function processPendingDeletions(options: PendingProcessOptions = {}): Promise<PendingProcessResult> {
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    UploadImageHelperLogger.Main.warn('[RECOVERY] Processamento de delecoes pendentes bloqueado');
    return { total: 0, processed: 0, success: 0, errors: 0 };
  }

  let snapshot: PendingSyncSessionSnapshot | null;
  try {
    snapshot = await resolvePendingSessionSnapshot(options.sessionSnapshot);
  } catch (error) {
    UploadImageHelperLogger.Main.info('Sessão mudou antes do processamento das deleções pendentes; fila preservada.', error);
    return { total: 0, processed: 0, success: 0, errors: 0 };
  }
  if (!snapshot) {
    UploadImageHelperLogger.Main.info('Usuário não autenticado, não processando deleções pendentes');
    return {
      total: 0,
      processed: 0,
      success: 0,
      errors: 0,
    };
  }

  UploadImageHelperLogger.Main.info('Processando deleções pendentes...');

  // Busca todas as pendências
  const allPendings = await databaseService.listItems<PendingDeletion>('pending_deletions');
  const pendings = allPendings.filter(pending =>
    pendingOwnedBy(pending, snapshot!) &&
    (!options.allowedPendingIds || options.allowedPendingIds.has(pending.id))
  );

  UploadImageHelperLogger.Main.info(`Encontradas ${pendings.length} deleções pendentes para processar`);

  let processed = 0;
  let success = 0;
  let errors = 0;
  let deferred = 0;

  if (pendings.length === 0) {
    options.onProgress?.({
      context: 'deletions',
      processed,
      success,
      errors,
      total: pendings.length,
    });
    return {
      total: pendings.length,
      processed,
      success,
      errors,
    };
  }

  //await debugPause('check 3');
  // Processa cada pendência
  let sessionChanged = false;
  for (const p of pendings) {
    //await debugPause('check 4');
    try {
      await assertPendingSyncSessionCurrent(snapshot);
      // Limpeza de checklist aguarda coordenação autoritativa; fila e bytes permanecem intactos.
      const cleanupType = String(p.itemType ?? (p as any).item_type ?? '').trim().toUpperCase();
      if (cleanupType === 'CHECKLIST') { deferred += 1; continue; }
      if (p.itemType !== 'USER' && p.itemType !== 'COMPANY') {
        throw new Error('Tipo de limpeza legado sem classificação física autorizada.');
      }
      await assertPendingDeletionTargetInScope(p, snapshot);
      UploadImageHelperLogger.Delete.info(`Processando deleção pendente: ${p.id}, tipo: ${p.type}`);

      const shouldDeleteRemote = await assertPendingChecklistDeletionIsSafe(
        p,
        options.tombstoneBarriers
      );

      if (p.type === 'LOCAL') {
        // Verificar existência do arquivo local antes de tentar excluir
        const exists = await fileExistsAsync(p.path);
      if (exists) {
        // Deletar arquivo local
        await assertPendingSyncSessionCurrent(snapshot);
        if (!isPendingSyncSessionSnapshotStateCurrent(snapshot)) {
          throw new Error('Sessão mudou antes da limpeza local; pendência preservada.');
        }
        UploadImageHelperLogger.Delete.info('Deletando arquivo local pendente');
          await deleteLocalFile(p.path);
          if (await fileExistsAsync(p.path)) {
            throw new Error('Arquivo local ainda existe após tentativa de exclusão; pendência preservada.');
          }
          UploadImageHelperLogger.Delete.info('Arquivo local deletado com sucesso');
        } else {
          UploadImageHelperLogger.Delete.info('Arquivo local não existe mais, pulando');
        }
      } else if (shouldDeleteRemote) {
        // Deletar arquivo remoto
        UploadImageHelperLogger.Delete.info('Deletando arquivo remoto no bucket configurado');
        const bucket = APP_CONSTANTS.STORAGE.BUCKET[p.itemType === 'USER' ? 'USER' : p.itemType === 'COMPANY' ? 'COMPANY' : 'CHECKLIST'];
        const pendingClient = createPendingSyncSupabaseClient(snapshot);
        await removeRemoteStorageObject(pendingClient, bucket, p.path, snapshot);
        UploadImageHelperLogger.Delete.info('Arquivo remoto deletado com sucesso');
      } else {
        UploadImageHelperLogger.Delete.info(
          'Limpeza remota terminalizada sem apagar Storage após deleção local comprovada do checklist'
        );
      }

      // Só confirma a fila se a sessão e o conteúdo ainda forem os mesmos.
      await assertPendingSyncSessionCurrent(snapshot);
      const deleted = await databaseService.deleteItemIfUnchanged('pending_deletions', p.id, {
        type: p.type,
        path: p.path,
        itemType: p.itemType,
        itemId: p.itemId,
        campo_id: p.campo_id ?? p.campoId ?? null,
        user_id: p.user_id ?? null,
        empresa_id: p.empresa_id ?? null,
      }, true, pendingSessionStateGuard(snapshot));
      if (!deleted) throw new Error('Pendência mudou antes do ACK local; registro mantido.');
      UploadImageHelperLogger.Delete.info(`Deleção pendente removida com sucesso: ${p.id}`);
      success += 1;
      //await debugPause('check 6');
    } catch (error) {
      if (!await isPendingSyncSessionCurrent(snapshot)) {
        sessionChanged = true;
        UploadImageHelperLogger.Delete.info(
          `[processPendingDeletions] Sessão mudou durante a tentativa ${p.id}; pendência preservada.`,
        );
      }
      UploadImageHelperLogger.Delete.error(`Falha ao processar deleção pendente: ${p.id}`, error);
      // Mantém a pendência para tentar novamente mais tarde
      errors += 1;
    } finally {
      processed += 1;
      options.onProgress?.({
        context: 'deletions',
        deferred,
        processed,
        success,
        errors,
        total: pendings.length,
      });
    }
    if (sessionChanged) break;
  }

  if (errors > 0) {
    captureSentryMessageWithScope('Processamento de deleções pendentes com falhas', {
      level: 'warning',
      context: {
        flow: 'pending_deletions',
        total: pendings.length,
        processed,
        success,
        errors,
      },
      tags: {
        flow: 'sync',
        stage: 'pending_deletions',
        result: 'partial',
      },
      fingerprint: ['sync', 'pending_deletions_partial'],
      dedupeKey: 'sync:pending_deletions_partial',
      dedupeWindowMs: 60000,
    });
  }

  return {
    total: pendings.length,
    processed,
    success,
    errors,
    ...(deferred ? { deferred } : {}),
  };
}

/**
 * Generic helper to delete images for USER, COMPANY e CHECKLIST
 * @param itemType Tipo do item ('USER', 'COMPANY', 'CHECKLIST')
 * @param itemId ID do item
 * @param columnName Obrigatório para USER/COMPANY
 * @param campoId Obrigatório para CHECKLIST
 * @param localPathColumn, previousLocalUri, previousImageUrl: opcionais
 */
export async function deleteImageHelper({
  itemType,
  itemId,
  columnName,
  campoId,
  localPathColumn,
  previousLocalUri,
  previousImageUrl
}: DeleteImageParams): Promise<{ success: boolean; error?: string }> {
  const origin = capturePendingWorkOrigin();
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    return { success: false, error: 'Exclusao de imagem bloqueada pelo modo de recuperacao.' };
  }
  if (!origin) {
    return { success: false, error: 'Não foi possível comprovar a autoria da operação de mídia.' };
  }

  const {
    DATABASE: { TABLES },
  } = APP_CONSTANTS;

  // Validação dos parâmetros conforme o tipo
  if (itemType === 'CHECKLIST') {
    if (!campoId) {
      return { success: false, error: 'campoId é obrigatório para CHECKLIST' };
    }
  } else {
    if (!columnName || !localPathColumn) {
      return { success: false, error: 'columnName e localPathColumn são obrigatórios para USER/COMPANY' };
    }
  }

  let sessionSnapshot: PendingSyncSessionSnapshot | null = null;
  if (itemType !== 'CHECKLIST') {
    try {
      sessionSnapshot = await capturePendingSyncSession();
    } catch {
      // A ação local pode continuar offline, mas nenhuma requisição remota será feita.
    }
  }
  if (sessionSnapshot && !samePendingWorkOrigin(origin, sessionSnapshot)) {
    sessionSnapshot = null;
  }
  if ((itemType === 'USER' && itemId !== origin.userId) ||
      (itemType === 'COMPANY' && itemId !== origin.empresaId)) {
    return { success: false, error: 'A imagem está fora do escopo da conta atual.' };
  }

  let checklistForDeletion: any = null;
  if (itemType === 'CHECKLIST') {
    checklistForDeletion = await databaseService.readItem<any>(
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
      itemId,
    );
    if (!checklistForDeletion?.empresa_id || checklistForDeletion.empresa_id !== origin.empresaId) {
      return { success: false, error: 'Checklist fora da empresa originadora da mídia.' };
    }
  }

  if (itemType === 'CHECKLIST' && campoId) {
    try {
      const existing = await findChecklistValueForVisualField(origin.empresaId, itemId, campoId, checklistForDeletion.snapshot_id);
      const expected = previousImageUrl ?? previousLocalUri ?? existing?.valor ?? '';
      await databaseService.bulkUpsertChecklistValues(origin.empresaId, itemId, { [campoId]: '' }, {
        skipPendingOp: true,
        mediaPending: { checklistId: itemId, campoId, pending: null, pendingOrigin: origin,
          cleanupId: uuid(), clearReference: true, expectedValue: String(expected),
          canAcknowledge: sessionSnapshot ? pendingSessionStateGuard(sessionSnapshot) : () => isPendingWorkOriginCurrent(origin) },
      });
      return { success: true };
    } catch (error) {
      UploadImageHelperLogger.Delete.error('Falha na remoção lógica; referência, fila e arquivos preservados.', error);
      return { success: false, error: 'Não foi possível registrar a remoção da mídia.' };
    }
  }

  // Somente USER/COMPANY usam a limpeza física legada.

  const tableName = itemType === 'USER' ? TABLES.USERS.NAME : itemType === 'COMPANY' ? TABLES.EMPRESAS.NAME : TABLES.CHECKLIST_PHOTOS.NAME;
  UploadImageHelperLogger.Delete.info(`Iniciando deleção da imagem do ${itemType.toLowerCase()}: ${itemId}`);

  // 1. Delete remote image (Supabase Storage)
  if (previousImageUrl) {
    try {
      UploadImageHelperLogger.Delete.info('Tentando excluir imagem remota');
      const { bucket, path } = extractPathFromUrl(previousImageUrl);
      if (!sessionSnapshot || !isPendingSyncSessionSnapshotStateCurrent(sessionSnapshot)) {
        throw new Error('Sessão validada indisponível; deleção foi enfileirada.');
      }
      await removeRemoteStorageObject(
        createPendingSyncSupabaseClient(sessionSnapshot),
        bucket,
        path,
        sessionSnapshot,
      );
      UploadImageHelperLogger.Delete.info(`Imagem remota excluída com sucesso.`);
    } catch (err) {
      UploadImageHelperLogger.Delete.warn(`Falha ao excluir imagem remota, registrando pendência: ${err}`);
      try {
        const { path } = extractPathFromUrl(previousImageUrl);
        await upsertPendingRemoteDeletion({ path, itemType, itemId, campoId }, origin);
        UploadImageHelperLogger.Delete.info('Pendência de deleção remota registrada ou atualizada sem duplicata');
      } catch (extractErr) {
        UploadImageHelperLogger.Delete.error(`Falha ao registrar pendência remota: ${extractErr}`);
      }
    }
  }

  // 2. Delete local image
  if (previousLocalUri) {
    try {
      UploadImageHelperLogger.Delete.info('Tentando excluir imagem local');
      const exists = await fileExistsAsync(previousLocalUri);
      if (exists) {
        assertPendingWorkOriginCurrent(origin);
        if (sessionSnapshot && !isPendingSyncSessionSnapshotStateCurrent(sessionSnapshot)) {
          throw new Error('Sessão mudou antes da limpeza do arquivo local.');
        }
        await deleteLocalFile(previousLocalUri);
        UploadImageHelperLogger.Delete.info(`Imagem local excluída com sucesso.`);
      } else {
        UploadImageHelperLogger.Delete.info('Arquivo local não existe mais');
      }
    } catch (err) {
      UploadImageHelperLogger.Delete.warn(`Falha ao excluir imagem local, registrando pendência: ${err}`);
      // Check for existing pending deletion
      const existing = await databaseService.listItems('pending_deletions', {
        type: 'LOCAL', path: previousLocalUri, itemType, itemId
      });
      const sameOrigin = (existing as PendingDeletion[]).filter(pending =>
        pending.user_id === origin.userId && pending.empresa_id === origin.empresaId
      );
      if (sameOrigin.length === 0) {
        await databaseService.createItem('pending_deletions', {
          id: uuid(),
          type: 'LOCAL',
          path: previousLocalUri,
          itemType,
          itemId,
          ...pendingQueueOriginFields(origin),
          createdAt: Date.now(),
        }, true);
        UploadImageHelperLogger.Delete.info(`Pendência de deleção local registrada.`);
      } else {
        UploadImageHelperLogger.Delete.info(`Pendência de deleção local já existente.`);
      }
    }
  }

  // 3. Update local database (clear image fields)
  try {
    assertPendingWorkOriginCurrent(origin);
    if (sessionSnapshot && !isPendingSyncSessionSnapshotStateCurrent(sessionSnapshot)) {
      throw new Error('Sessão mudou antes da atualização local da imagem.');
    }
    if (itemType === 'CHECKLIST' && campoId) {
      const checklist = checklistForDeletion;
      const existingRecords = await databaseService.listItems<ChecklistValueRecord>(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
        { checklist_id: itemId }
      );
      const snapshotField = await findChecklistSnapshotField(
        checklist.empresa_id,
        checklist.snapshot_id,
        campoId
      );
      const identity = resolveChecklistValueIdentity(campoId, snapshotField);
      const existingRecord = findChecklistValueByIdentity(existingRecords, identity, campoId);
      const preferCreate = !existingRecord || !isRemoteChecklistValue(existingRecord.valor);
      const mode: ChecklistPersistMode = preferCreate ? 'create' : 'update';
      assertPendingWorkOriginCurrent(origin);
      if (sessionSnapshot && !isPendingSyncSessionSnapshotStateCurrent(sessionSnapshot)) {
        throw new Error('Sessão mudou antes da atualização local da imagem.');
      }
      await persistChecklistValue({
        empresaId: checklist.empresa_id,
        checklistId: itemId,
        campoId,
        valor: '',
        mode,
        existing: existingRecord,
        snapshotId: checklist.snapshot_id,
        snapshotField,
        pendingOrigin: origin,
        canAcknowledge: sessionSnapshot
          ? pendingSessionStateGuard(sessionSnapshot)
          : () => isPendingWorkOriginCurrent(origin),
      });
      UploadImageHelperLogger.Delete.info(
        `CHECKLIST: Valor do campo limpo na tabela guincho_valores_checklist (modo=${mode})`
      );
    } else {
      const referenceCleared = await updateImageRowIfCurrent(
        tableName,
        itemId,
        { [columnName!]: null, [localPathColumn!]: null },
        origin,
        sessionSnapshot,
        false,
      );
      if (!referenceCleared) throw new Error('Referência da mídia mudou ou a sessão trocou antes da limpeza local.');
      UploadImageHelperLogger.Delete.info(`Campos de imagem limpos no banco local.`);
    }
  } catch (err) {
    UploadImageHelperLogger.Delete.error(`Erro ao atualizar banco local: ${err}`);
    return { success: false, error: 'Erro ao atualizar banco local.' };
  }

  // 4. Remove related upload pending items (pending_images)
  try {
    let pendings;
    if (itemType === 'CHECKLIST' && campoId) {
      pendings = await databaseService.listItems('pending_images', {
        item_id: itemId,
        item_type: itemType,
        campo_id: campoId
      });
    } else {
      pendings = await databaseService.listItems('pending_images', {
        item_id: itemId,
        item_type: itemType,
        column_name: columnName
      });
    }
    const canAcknowledge = sessionSnapshot
      ? pendingSessionStateGuard(sessionSnapshot)
      : () => isPendingWorkOriginCurrent(origin);
    for (const p of (pendings as PendingImage[]).filter(pending =>
      pending.user_id === origin.userId && pending.empresa_id === origin.empresaId
    )) {
      assertPendingWorkOriginCurrent(origin);
      if (sessionSnapshot && !isPendingSyncSessionSnapshotStateCurrent(sessionSnapshot)) {
        throw new Error('Sessão mudou antes do ACK local da mídia.');
      }
      await databaseService.deleteItemIfUnchanged('pending_images', p.id, {
        item_type: p.item_type,
        item_id: p.item_id,
        column_name: p.column_name ?? null,
        campo_id: p.campo_id ?? null,
        local_uri: p.local_uri,
        user_id: origin.userId,
        empresa_id: origin.empresaId,
      }, true, canAcknowledge);
      UploadImageHelperLogger.Delete.info(`Pendência de upload removida: ${p.id}`);
    }
  } catch (err) {
    UploadImageHelperLogger.Delete.warn(`Erro ao remover pendências de upload: ${err}`);
  }

  UploadImageHelperLogger.Delete.info(`Deleção da imagem concluída.`);
  return { success: true };
}
